import type { Catalogo } from '../catalogo';

// El catálogo en inglés.
//
// **`: Catalogo` es la línea que hace todo el trabajo.** Obliga a que este
// archivo tenga exactamente las mismas claves que el español, con la misma
// forma: si se agrega un texto allá y no acá, **no compila**, y el pipeline lo
// frena antes de que llegue a dev.
//
// Ese tipo vive en `../catalogo.ts` y no es `typeof es` a secas por un motivo
// que está explicado ahí: `typeof es` exigiría el mismo TEXTO, no las mismas
// claves.
//
// Eso es lo que no hace una librería de traducción: las de uso común muestran la
// clave —`admin.inicio.titulo`— en pantalla y siguen como si nada. El error
// aparece cuando lo ve un cliente, no cuando se escribió.
//
// El precio de esto, dicho claro: no se puede dejar una traducción a medias. Si
// no hay texto todavía, se pone el español mientras tanto — queda feo y visible,
// que es exactamente lo que uno quiere de algo sin terminar.
export const en: Catalogo = {
  // --- Lo común a todas las apps ---
  'app.marca': 'TallerYa',
  'app.cargando': 'Loading…',
  'app.reintentar': 'Retry',
  'app.guardar': 'Save',
  'app.cancelar': 'Cancel',
  'app.idioma': 'Language',

  // --- El patrón de listado de @tallerya/ui (F-7) ---
  'ui.listado.mostrando': {
    one: 'Showing 1 row',
    other: 'Showing {n} rows',
  },
  'ui.listado.paginacion': 'Pagination',
  'ui.listado.paginaAnterior': 'Previous page',
  'ui.listado.paginaSiguiente': 'Next page',
  'ui.listado.acciones': 'Actions',
  'ui.listado.accionesFila': 'Row actions',
  'ui.listado.limpiarFiltros': 'Clear filters',
  'ui.listado.sinResultados.titulo': 'No results found',
  'ui.listado.sinResultados.texto': 'Check the spelling or try fewer words.',
  'ui.listado.error.titulo': 'The list could not be loaded',
  'ui.listado.error.texto':
    'It may be the connection or the server. Try again; if it keeps happening, contact support.',

  // --- El ⓘ de un título o un campo (#120) ---
  'ui.ayuda.abrir': 'More information about {tema}',

  'ui.campo.maximo': 'Up to {maximo} characters',
  'ui.campo.cerca': 'Approaching the {maximo}-character limit',
  'ui.campo.excedido': 'Over the {maximo}-character limit',

  // --- El patrón de formulario de @tallerya/ui (F-8) ---
  'ui.formulario.guardando': 'Saving…',
  'ui.formulario.guardarYOtro': 'Save and add another',
  'ui.formulario.aviso.titulo': 'Could not save',
  'ui.formulario.aviso.campos': {
    one: 'Check the field marked in red.',
    other: 'Check the {n} fields marked in red.',
  },
  'ui.formulario.aviso.sinRespuesta':
    'The server did not respond. Check your connection and try saving again: what you typed is still here.',
  'ui.formulario.aviso.fueraDePantalla':
    'It could not be saved because of a value that is not on this screen: {detalle}. If it happens again, contact support.',
  'ui.formulario.recargar': 'Reload',
  'ui.avisos.cerrar': 'Close notice',
  'ui.panel.cerrar': 'Close',

  // --- Menú lateral del sitio administrativo (#98) ---
  'admin.nav.inicio': 'Dashboard',
  'admin.nav.catalogo': 'Products/Services',
  'admin.nav.clientes': 'Customers',
  'admin.nav.activos': 'Assets',
  'admin.nav.personal': 'Mechanics/Operators',
  'admin.nav.ordenes': 'Service orders',
  'admin.nav.gastos': 'Expenses',
  'admin.nav.agendas': 'Schedules',
  'admin.nav.reportes': 'Reports',
  'admin.nav.configuracion': 'Settings',
  'admin.nav.sucursales': 'Locations',
  'admin.nav.marcas': 'Brands and models',
  'admin.nav.catalogos': 'Catalogs',
  'admin.nav.estadosDeOrden': 'Order statuses',
  'admin.nav.proveedores': 'Suppliers',
  'admin.nav.cerrarSesion': 'Sign out',
  'admin.nav.pronto': 'Coming soon',

  // --- Armazón: app bar y barra derecha (#99, #100) ---
  'admin.layout.menu': 'Navigation menu',
  'admin.layout.panel': 'Side bar',
  'admin.layout.soporte': 'Support',
  'admin.layout.conectado': 'Online',
  'admin.layout.sinConexion': 'Offline',
  'admin.layout.tema': 'Light theme',

  // --- Selector de sucursal del app bar (#101) ---
  'admin.sucursal.grupo': 'Branch',
  'admin.sucursal.actual': 'Branch: {nombre}',
  'admin.sucursal.principal': 'Main',

  // --- Workshop selector (#205) ---
  'admin.taller.grupo': 'Workshop',
  'admin.taller.actual': 'Workshop: {nombre}',
  'admin.taller.error.titulo': 'Could not read your workshops',
  'admin.taller.error.texto': 'Try again. If it keeps happening, tell whoever manages your workshop.',

  // --- Pantalla de inicio ---
  'admin.inicio.titulo': 'Shop administration',
  'admin.inicio.entrada':
    'This is where the shop owner sets up their locations, staff, customers, and their catalogue of services and parts.',
  'admin.inicio.aviso.titulo': "Today's summary is on its way",
  'admin.inicio.aviso.texto':
    "This is where you will see open orders, the ones ready for pickup and today's appointments. In the meantime, everything else is in the menu.",

  // --- Sesión (F-5) ---
  'admin.sesion.entrar': 'Sign in',
  'admin.sesion.salir': 'Sign out',
  'admin.sesion.error': 'Could not sign in.',
  'admin.sesion.cuenta': 'Your account: {nombre}',
  'admin.acceso.sinTaller.titulo': 'This user does not belong to any workshop',
  'admin.acceso.sinTaller.texto':
    'The admin site belongs to each workshop, and this account does not belong to any. Sign out and sign in with the email your workshop registered you with.',
  'admin.acceso.sinTaller.cuenta': 'Signed in as {nombre}.',

  // --- Guardas de ruta (F-6) ---
  'admin.guardia.sinSesion.titulo': 'You need to sign in',
  'admin.guardia.sinSesion.texto': 'This screen belongs to the shop, so we need to know who you are.',
  'admin.guardia.sinPermiso.titulo': 'You cannot see this',
  'admin.guardia.sinPermiso.texto':
    'Your role in the shop does not include this screen. If you need it, ask whoever administers the shop.',
  'admin.guardia.suspendido.titulo': 'Workshop suspended',
  'admin.guardia.error.titulo': 'Could not read your permissions',
  'admin.guardia.error.texto':
    'You may have been removed from this shop, or the API is not responding. Sign in again; if it persists, contact support.',

  // --- Estado de la conexión con la API (F-4) ---
  'admin.api.titulo': 'API connection',
  'admin.api.consultando': 'Asking the API…',
  'admin.api.sinSesion': 'The API answers and asks for credentials. Sign in to see your shop.',
  'admin.api.conSesion': 'Signed in at shop {taller}.',
  'admin.api.sinApi': 'Could not reach the API at {direccion}. Is it running?',

  // --- Sucursales (M-1) ---
  'admin.sucursales.titulo': 'Locations',
  'admin.sucursales.info': 'The workshop\'s locations. Every order is opened at a location, and staff are assigned by location.',
  'admin.sucursales.buscar': 'Search by name or code',
  'admin.sucursales.nueva': 'New location',
  'admin.sucursales.filtro.estado': 'Status',
  'admin.sucursales.filtro.todas': 'All',
  'admin.sucursales.columna.codigo': 'Code',
  'admin.sucursales.columna.nombre': 'Name',
  'admin.sucursales.columna.direccion': 'Address',
  'admin.sucursales.columna.telefono': 'Phone',
  'admin.sucursales.columna.personal': 'Staff',
  'admin.sucursales.columna.estado': 'Status',
  'admin.sucursales.principal': 'Main',
  'admin.sucursales.enServicio': 'In service',
  'admin.sucursales.fueraDeServicio': 'Out of service',
  'admin.sucursales.vacio.titulo': 'No locations yet',
  'admin.sucursales.vacio.texto':
    'A location is one of the shop premises: every service order is opened at one and staff are assigned per location. The first one you create will be the main one.',
  'admin.sucursales.sinResultados': 'No location matches “{texto}”.',

  'admin.sucursales.menu.editar': 'Edit',
  'admin.sucursales.menu.asignarPersonal': 'Assign staff',
  'admin.sucursales.menu.nombrarPrincipal': 'Make main',
  'admin.sucursales.menu.fueraDeServicio': 'Take out of service',
  'admin.sucursales.menu.enServicio': 'Put back in service',
  'admin.sucursales.menu.eliminar': 'Delete',

  'admin.sucursales.nueva.descripcion':
    'The first location of the shop becomes the main one. Fields marked with * are required.',
  'admin.sucursales.editar.titulo': 'Edit location',
  'admin.sucursales.editar.info': 'Correct the location\'s details. Fields marked with * are required.',
  'admin.sucursales.formulario.seccion': 'General information',
  'admin.sucursales.formulario.seccionTexto': 'How the location is identified in orders and reports.',
  'admin.sucursales.formulario.codigo': 'Code',
  'admin.sucursales.formulario.codigoAyuda': 'Leave it empty to use the next number in sequence.',
  'admin.sucursales.formulario.nombre': 'Name',
  'admin.sucursales.formulario.nombreEjemplo': 'E.g. Downtown',
  'admin.sucursales.formulario.telefono': 'Phone',
  'admin.sucursales.formulario.direccion': 'Address',
  'admin.sucursales.formulario.direccionEjemplo': 'Street, number, neighborhood and city',
  'admin.sucursales.formulario.guardarCambios': 'Save changes',
  'admin.sucursales.formulario.eliminar': 'Delete location',
  'admin.sucursales.noExiste.titulo': 'This location does not exist',
  'admin.sucursales.noExiste.texto': 'It may have been deleted. Go back to the list to see the shop locations.',
  'admin.sucursales.volver': 'Back to locations',

  'admin.sucursales.asignar.titulo': 'Staff at {nombre}',
  'admin.sucursales.asignar.texto': 'Check who works at this location. A person can be at several.',
  'admin.sucursales.asignar.seleccionadas': { one: '1 selected', other: '{n} selected' },
  'admin.sucursales.asignar.sinPersonal':
    'There is no active staff yet. Add your mechanics in Staff and come back here to assign them.',
  'admin.sucursales.asignar.error': 'Could not read the staff. Close the window and try again.',

  'admin.sucursales.eliminar.titulo': 'Delete location',
  'admin.sucursales.eliminar.texto':
    'You are about to delete {nombre} ({codigo}). It will no longer appear when opening orders or assigning staff; its past orders are kept. Its code and name become available.',
  'admin.sucursales.eliminar.boton': 'Delete',

  'admin.sucursales.aviso.creada': 'Location {nombre} was created',
  'admin.sucursales.aviso.guardada': 'Changes to {nombre} were saved',
  'admin.sucursales.aviso.principal': '{nombre} is now the main location',
  'admin.sucursales.aviso.fueraDeServicio': '{nombre} is now out of service',
  'admin.sucursales.aviso.enServicio': '{nombre} is back in service',
  'admin.sucursales.aviso.eliminada': 'Location {nombre} was deleted',
  'admin.sucursales.aviso.personal': 'Staff at {nombre} was saved',
  'admin.sucursales.error.titulo': 'Could not complete',

  // --- Suppliers (M-2, #56) ---
  'admin.proveedores.titulo': 'Suppliers',
  'admin.proveedores.info':
    'Who the shop buys parts and supplies from. Chosen when recording an expense; an inactive supplier is no longer offered.',
  'admin.proveedores.buscar': 'Search by name, code or document',
  'admin.proveedores.nuevo': 'New supplier',
  'admin.proveedores.filtro.estado': 'Status',
  'admin.proveedores.filtro.todos': 'All',
  'admin.proveedores.filtro.activos': 'Active',
  'admin.proveedores.filtro.inactivos': 'Inactive',
  'admin.proveedores.columna.codigo': 'Code',
  'admin.proveedores.columna.nombre': 'Name',
  'admin.proveedores.columna.documento': 'Document',
  'admin.proveedores.columna.referencia': 'Reference',
  'admin.proveedores.columna.estado': 'Status',
  'admin.proveedores.activo': 'Active',
  'admin.proveedores.inactivo': 'Inactive',
  'admin.proveedores.vacio.titulo': 'No suppliers yet',
  'admin.proveedores.vacio.texto':
    'A supplier is who the shop buys parts and supplies from. You pick one when recording an expense, and if it does not exist yet, you create it right there.',
  'admin.proveedores.sinResultados': 'No supplier matches “{texto}”.',

  'admin.proveedores.menu.verFicha': 'View details',
  'admin.proveedores.menu.editar': 'Edit',
  'admin.proveedores.menu.desactivar': 'Deactivate',
  'admin.proveedores.menu.activar': 'Activate',
  'admin.proveedores.menu.eliminar': 'Delete',

  'admin.proveedores.nuevo.info':
    'The name is enough: you can fill in the rest later. Fields marked with * are required.',
  'admin.proveedores.editar.titulo': 'Edit supplier',
  'admin.proveedores.editar.info': 'Fix the supplier details. Fields marked with * are required.',
  'admin.proveedores.formulario.seccion': 'General information',
  'admin.proveedores.formulario.seccionInfo': 'How the supplier is recognized when recording an expense.',
  'admin.proveedores.formulario.codigo': 'Code',
  'admin.proveedores.formulario.codigoAyuda': 'Assigned automatically. You can replace it.',
  'admin.proveedores.formulario.nombre': 'Name',
  'admin.proveedores.formulario.nombreEjemplo': 'E.g. Southern Parts',
  'admin.proveedores.formulario.tipoDocumento': 'Document type',
  'admin.proveedores.formulario.sinDocumento': 'No document',
  'admin.proveedores.formulario.numeroDocumento': 'Document number',
  'admin.proveedores.formulario.referencia': 'Reference',
  'admin.proveedores.formulario.referenciaAyuda':
    'The account or customer number the supplier gave you. It is what you read out when placing an order.',
  'admin.proveedores.formulario.referenciaEjemplo': 'E.g. C-88412',
  'admin.proveedores.formulario.guardarCambios': 'Save changes',
  'admin.proveedores.formulario.eliminar': 'Delete supplier',
  'admin.proveedores.noExiste.titulo': 'This supplier does not exist',
  'admin.proveedores.noExiste.texto':
    'It may have been deleted. Go back to the list to see the shop’s suppliers.',
  'admin.proveedores.volver': 'Back to suppliers',
  'admin.proveedores.ficha.info':
    'The supplier’s details and who changed them. To correct something, use «Edit supplier».',
  'admin.proveedores.ficha.desde': 'Supplier since {fecha}',
  'admin.proveedores.mas': 'More actions',
  'admin.proveedores.historial.info':
    'Who registered the supplier and who changed what, newest first. The code, the name and the document number show that they changed, but not to what: a supplier can be a person, and they are not stored.',

  'admin.proveedores.eliminar.titulo': 'Delete supplier',
  'admin.proveedores.eliminar.texto':
    'You are about to delete {nombre} ({codigo}). It will no longer show up when recording expenses; expenses that already use it are kept. Its code, name and document become free.',
  'admin.proveedores.eliminar.boton': 'Delete supplier',

  'admin.proveedores.vuelo.titulo': 'New supplier',
  'admin.proveedores.vuelo.info':
    'It is created with the name and the code is assigned automatically. Fill in the rest later in Suppliers.',
  'admin.proveedores.vuelo.crear': 'Create supplier',

  'admin.proveedores.aviso.creado': 'Supplier {nombre} created',
  'admin.proveedores.aviso.guardado': 'Changes to {nombre} saved',
  'admin.proveedores.aviso.activado': '{nombre} is active again',
  'admin.proveedores.aviso.desactivado': '{nombre} is now inactive',
  'admin.proveedores.aviso.eliminado': 'Supplier {nombre} deleted',
  'admin.proveedores.error.titulo': 'Could not complete',
  'admin.proveedores.error.repetidoNombre': '{nombre} ({codigo}) is already registered with that name.',
  'admin.proveedores.error.repetidoDocumento': '{nombre} ({codigo}) is already registered with that document.',
  'admin.proveedores.error.repetidoAmbos': '{nombre} ({codigo}) is already registered with that name and that document.',
  'admin.proveedores.error.verExistente': 'See existing supplier →',

  // --- Personal (M-4, #58) ---
  'admin.personal.titulo': 'Mechanics/Operators',
  'admin.personal.info':
    'The shop staff: mechanics, front desk and administration. Active people can be assigned work in orders.',
  'admin.personal.buscar': 'Search by name or code',
  'admin.personal.nuevo': 'New mechanic or operator',
  'admin.personal.filtro.especialidad': 'Specialty',
  'admin.personal.filtro.sucursal': 'Branch',
  'admin.personal.filtro.estado': 'Status',
  'admin.personal.filtro.todas': 'All',
  'admin.personal.filtro.todos': 'All',
  'admin.personal.filtro.activos': 'Active',
  'admin.personal.filtro.inactivos': 'Inactive',
  'admin.personal.columna.codigo': 'Code',
  'admin.personal.columna.nombre': 'Name',
  'admin.personal.columna.especialidades': 'Specialties',
  'admin.personal.columna.sucursales': 'Branch',
  'admin.personal.columna.telefono': 'Phone',
  'admin.personal.columna.estado': 'Status',
  'admin.personal.activo': 'Active',
  'admin.personal.inactivo': 'Inactive',
  'admin.personal.vacio.titulo': 'No staff registered yet',
  'admin.personal.vacio.texto':
    'Add your mechanics and operators with their specialties, so you know who to assign each job to.',
  'admin.personal.sinResultados': 'Nobody matches «{texto}».',

  'admin.personal.menu.verFicha': 'View details',
  'admin.personal.menu.editar': 'Edit',
  'admin.personal.menu.cambiarCodigo': 'Change code',
  'admin.personal.menu.desactivar': 'Deactivate',
  'admin.personal.menu.activar': 'Activate',
  'admin.personal.menu.eliminar': 'Delete',

  'admin.personal.nuevo.info':
    'First and last name are enough: you can fill in the rest later. Fields marked with * are required.',
  'admin.personal.editar.titulo': 'Edit mechanic or operator',
  'admin.personal.editar.info':
    'Correct the person’s details. The code is changed from their details page, because it is their sign-in user. Fields marked with * are required.',
  'admin.personal.formulario.datos': 'Personal details',
  'admin.personal.formulario.datosInfo': 'How the person is recognized in orders and in the shop.',
  'admin.personal.formulario.nombre': 'First name',
  'admin.personal.formulario.nombreEjemplo': 'E.g. Carlos',
  'admin.personal.formulario.apellido': 'Last name',
  'admin.personal.formulario.apellidoEjemplo': 'E.g. Méndez',
  'admin.personal.formulario.codigo': 'Code',
  'admin.personal.formulario.codigoAyuda': 'Assigned automatically; you can replace it. It is their sign-in user.',
  'admin.personal.formulario.ingreso': 'Hire date',
  'admin.personal.formulario.telefono': 'Phone',
  'admin.personal.formulario.correo': 'Email',
  'admin.personal.formulario.correoAyuda': 'Optional: many mechanics have none. Their code is enough to sign in.',
  'admin.personal.formulario.trabajo': 'Work',
  'admin.personal.formulario.trabajoInfo': 'What they can do and where they work: it decides who gets each job.',
  'admin.personal.formulario.especialidades': 'Specialties',
  'admin.personal.formulario.especialidadesAyuda':
    'Type one and press Enter. If it does not exist, it is created on save: «electrical», «bodywork»…',
  'admin.personal.formulario.especialidadesPlaceholder': 'Type and press Enter',
  'admin.personal.formulario.quitar': 'Remove',
  'admin.personal.formulario.sucursales': 'Branches',
  'admin.personal.formulario.sucursalesAyuda':
    'Where they work; it can be more than one. With none checked, they are not limited to a branch.',
  'admin.personal.formulario.guardarCambios': 'Save changes',
  'admin.personal.formulario.eliminar': 'Delete',
  'admin.personal.noExiste.titulo': 'This person does not exist',
  'admin.personal.noExiste.texto': 'They may have been deleted. Go back to the list to see the shop staff.',
  'admin.personal.volver': 'Back to Mechanics/Operators',

  'admin.personal.ficha.info':
    'The person’s details, specialties and where they work. To correct something, use «Edit».',
  'admin.personal.ficha.desde': 'Registered on {fecha}',
  'admin.personal.ficha.datos': 'Details',
  'admin.personal.ficha.datosInfo': 'How to reach them, since when they work at the shop and what they can do.',
  'admin.personal.ficha.sinCorreo': '— (none)',
  'admin.personal.ficha.sinSucursales': 'All',
  'admin.personal.ficha.sinEspecialidades': 'No specialties',
  'admin.personal.mas': 'More actions',

  'admin.personal.codigo.titulo': 'Change code',
  'admin.personal.codigo.info':
    'The code is the user the person signs in with. Changing it renames their user, and from then on they sign in with the new code. If the identity system does not respond, the change completes by itself when it is back.',
  'admin.personal.codigo.actual': 'Current code: {codigo}',
  'admin.personal.codigo.nuevo': 'New code',
  'admin.personal.codigo.boton': 'Change code',

  'admin.personal.eliminar.titulo': 'Delete',
  'admin.personal.eliminar.texto':
    'You are about to delete {nombre} ({codigo}). Deleting is only for someone registered by mistake. Their code is not freed, because their user still exists.',
  'admin.personal.eliminar.boton': 'Delete',
  'admin.personal.eliminar.bloqueado.titulo': 'Cannot be deleted',
  'admin.personal.eliminar.bloqueado.activo':
    '{nombre} {historial}. Deleting is only for someone registered by mistake. If they no longer work at the shop, deactivate them: their history is kept and they are no longer offered for assignments.',
  'admin.personal.eliminar.bloqueado.inactivo':
    '{nombre} {historial}. Deleting is only for someone registered by mistake. They are already deactivated: their history is kept and they get no assignments.',
  'admin.personal.eliminar.bloqueado.ordenes': { one: 'appears in 1 order', other: 'appears in {n} orders' },
  'admin.personal.eliminar.bloqueado.cambios': 'has already recorded changes in the system',
  'admin.personal.eliminar.entendido': 'Got it',
  'admin.personal.eliminar.desactivar': 'Deactivate',

  'admin.personal.aviso.creado': '{nombre} was registered with code {codigo}',
  'admin.personal.aviso.guardado': 'Changes to {nombre} were saved',
  'admin.personal.aviso.codigo': '{nombre}’s code is now {codigo}',
  'admin.personal.aviso.activado': '{nombre} is active again',
  'admin.personal.aviso.desactivado': '{nombre} is now inactive',
  'admin.personal.aviso.eliminado': '{nombre} was deleted',
  'admin.personal.error.titulo': 'Could not complete',

  // --- Productos/Servicios (M-3, #112) ---
  'admin.catalogo.titulo': 'Products/Services',
  'admin.catalogo.info': 'The services and products the workshop offers, with their list price. They are what gets added to orders; inactive ones are no longer offered.',
  'admin.catalogo.nuevo': 'New product or service',
  'admin.catalogo.buscar': 'Search by code or name',
  'admin.catalogo.filtro.tipo': 'Type',
  'admin.catalogo.filtro.categoria': 'Category',
  'admin.catalogo.filtro.estado': 'Status',
  'admin.catalogo.filtro.todos': 'All',
  'admin.catalogo.filtro.todas': 'All',
  'admin.catalogo.columna.codigo': 'Code',
  'admin.catalogo.columna.nombre': 'Name',
  'admin.catalogo.columna.tipo': 'Type',
  'admin.catalogo.columna.categoria': 'Category',
  'admin.catalogo.columna.precio': 'Price',
  'admin.catalogo.columna.duracion': 'Duration',
  'admin.catalogo.columna.estado': 'Status',
  'admin.catalogo.servicio': 'Service',
  'admin.catalogo.producto': 'Product',
  'admin.catalogo.servicios': 'Services',
  'admin.catalogo.productos': 'Products',
  'admin.catalogo.activo': 'Active',
  'admin.catalogo.inactivo': 'Inactive',
  'admin.catalogo.vacio.titulo': 'No products or services yet',
  'admin.catalogo.vacio.texto':
    'Add what the workshop offers so it can be added to orders: oil changes, brakes, diagnostics, parts.',
  'admin.catalogo.sinResultados': 'No item matches «{texto}».',
  'admin.catalogo.sinResultados.conFiltros': {
    one: 'No item matches «{texto}» and the filter {filtros}.',
    other: 'No item matches «{texto}» and the filters {filtros}.',
  },
  'admin.catalogo.sinResultados.buscarYCombos': 'Check the spelling or remove the filters to see more results.',
  'admin.catalogo.sinResultados.combos':
    'No item matches the selected type, category or status. Change them or clear the filters.',
  'admin.catalogo.sinResultados.pagina': 'There are no items left on this page. Go back to the start of the list.',
  'admin.catalogo.menu.verFicha': 'View details',
  'admin.catalogo.menu.editar': 'Edit',
  'admin.catalogo.menu.duplicar': 'Duplicate',
  'admin.catalogo.menu.activar': 'Activate',
  'admin.catalogo.menu.desactivar': 'Deactivate',
  'admin.catalogo.menu.eliminar': 'Delete',
  'admin.catalogo.mas': 'More actions',
  'admin.catalogo.menu.importarArchivo': 'Import from file',
  'admin.catalogo.exportar.grupo': 'Export',
  'admin.catalogo.exportar.aExcel': 'Export to Excel',
  'admin.catalogo.exportar.aCsv': 'Export to CSV',
  'admin.catalogo.exportar.aPdf': 'Export to PDF',
  'admin.catalogo.exportar.nota': 'What the current filter shows is exported.',
  'admin.catalogo.exportar.excel': 'Excel',
  'admin.catalogo.exportar.csv': 'CSV',
  'admin.catalogo.importar.titulo': 'Import products and services',
  'admin.catalogo.importar.descripcion': 'Upload an Excel or CSV file with one product or service per row.',
  'admin.catalogo.importar.columnas': 'Columns: Código, Tipo, Nombre, Categoría, Precio and Duración (min). Only Tipo and Nombre are required; without a code, one is assigned. Categories that do not exist are created.',
  'admin.catalogo.importar.plantilla': 'Download template:',
  'admin.catalogo.importar.plantillaError': 'Could not download the template. Try again.',
  'admin.catalogo.importar.zona': 'Drag the file here or',
  'admin.catalogo.importar.elegir': 'choose it',
  'admin.catalogo.importar.formatos': 'Excel (.xlsx) or CSV, up to 5 MB',
  'admin.catalogo.importar.revisando': 'Checking {archivo}…',
  'admin.catalogo.importar.tipoInvalido': 'The file must be Excel (.xlsx) or CSV.',
  'admin.catalogo.importar.muyGrande': 'The file is over 5 MB. Split it and try again.',
  'admin.catalogo.importar.error.titulo': 'Could not check the file',
  'admin.catalogo.importar.error.importar': 'Could not import',
  'admin.catalogo.importar.leidas': { one: '1 row was read from {archivo}.', other: '{n} rows were read from {archivo}.' },
  'admin.catalogo.importar.listos': { one: '1 item ready', other: '{n} items ready' },
  'admin.catalogo.importar.conErrores': { one: '1 row with errors', other: '{n} rows with errors' },
  'admin.catalogo.importar.nuevasCategorias': 'These categories will be created: {lista}.',
  'admin.catalogo.importar.columna.fila': 'Row',
  'admin.catalogo.importar.columna.nombre': 'Name',
  'admin.catalogo.importar.columna.problema': 'Problem',
  'admin.catalogo.importar.nada': 'No row can be imported. Fix the file and upload it again.',
  'admin.catalogo.importar.otro': 'Choose another file',
  'admin.catalogo.importar.cancelar': 'Cancel',
  'admin.catalogo.importar.cerrar': 'Close',
  'admin.catalogo.importar.boton': { one: 'Import the valid one', other: 'Import the {n} valid ones' },
  'admin.catalogo.importar.hecho': { one: '1 product or service was imported', other: '{n} products and services were imported' },
  'admin.catalogo.importar.cambiaron': {
    one: '1 row changed since the review and was left out.',
    other: '{n} rows changed since the review and were left out.',
  },
  'admin.catalogo.aviso.activado': '«{nombre}» is active again',
  'admin.catalogo.aviso.desactivado': '«{nombre}» is now inactive',
  'admin.catalogo.aviso.eliminado': '«{nombre}» was deleted from the catalog',
  'admin.catalogo.error.titulo': 'The action could not be completed',
  'admin.catalogo.error.reintentar': 'Try again in a moment. If it keeps failing, check your connection.',
  'admin.catalogo.editar.titulo': 'Edit service',
  'admin.catalogo.editar.tituloProducto': 'Edit product',
  'admin.catalogo.editar.info': 'The new price applies to new orders: the ones that already have it keep the price that was charged. Fields marked with * are required.',
  'admin.catalogo.formulario.entrada':
    'Add what the workshop offers. Fields marked with * are required.',
  'admin.catalogo.formulario.general': 'General information',
  'admin.catalogo.formulario.tipo': 'Type',
  'admin.catalogo.formulario.codigo': 'Code',
  'admin.catalogo.formulario.codigoAyuda': 'Assigned automatically. You can replace it.',
  'admin.catalogo.formulario.codigoAvisoEdicion':
    'If you change it, documents printed with the previous code will no longer find it.',
  'admin.catalogo.formulario.nombre': 'Name',
  'admin.catalogo.formulario.categoria': 'Category',
  'admin.catalogo.formulario.sinCategoria': 'No category',
  'admin.catalogo.formulario.agregarCategoria': 'Add category',
  'admin.catalogo.formulario.categoriaInactiva': '(Deactivated category)',
  'admin.catalogo.formulario.codigoExterno': 'External code',
  'admin.catalogo.formulario.codigoExternoAyuda': 'The supplier or box code. Optional.',
  'admin.catalogo.formulario.precioYTiempo': 'Price and time',
  'admin.catalogo.formulario.precioYTiempoAyuda':
    'These are reference values: each order can adjust them.',
  'admin.catalogo.formulario.precio': 'List price',
  'admin.catalogo.formulario.precioAvisoEdicion':
    'Changing the price does not change orders already registered.',
  'admin.catalogo.formulario.duracion': 'Estimated duration',
  'admin.catalogo.formulario.horas': 'hours',
  'admin.catalogo.formulario.voz': 'Voice recognition',
  'admin.catalogo.formulario.vozAyuda':
    'How people call it. Helps the dictation of the mechanic find this service.',
  'admin.catalogo.formulario.otrosNombres': 'Other names',
  'admin.catalogo.formulario.otrosNombresPlaceholder': 'Type and press Enter',
  'admin.catalogo.formulario.quitar': 'Remove',
  'admin.catalogo.formulario.estado': 'Status',
  'admin.catalogo.formulario.activoAyuda':
    'Available to be added to orders. Turn it off when it is no longer offered.',
  'admin.catalogo.formulario.guardarCambios': 'Save changes',
  'admin.catalogo.formulario.desactivar': 'Deactivate',
  'admin.catalogo.formulario.creado': 'Created on {fecha}',
  'admin.catalogo.formulario.modificado': 'Last modified: {fecha}',
  'admin.catalogo.formulario.modificadoPor': 'Last modified: {fecha} by {autor}',
  'admin.catalogo.formulario.hoy': 'today',
  'admin.catalogo.noExiste.titulo': 'This product or service does not exist',
  'admin.catalogo.noExiste.texto': 'It may have been deleted, or the link belongs to another workshop.',
  'admin.catalogo.volver': 'Back to Products/Services',
  'admin.catalogo.ficha.migas': 'Breadcrumb',
  'admin.catalogo.ficha.info': 'Everything about this service or product, without opening it to edit. From here it can be edited, duplicated, deactivated or deleted.',
  'admin.catalogo.ficha.sinAlias': 'No other names. Add them when editing so the mechanic\'s dictation finds it.',
  'admin.catalogo.ficha.uso': 'Use in orders',
  'admin.catalogo.historial.info':
    'Who created this service or product and who changed what, from which value to which, newest first.',
  'admin.catalogo.ficha.sinOrdenesTexto': 'It can still be deleted: it touches no history.',
  'admin.catalogo.ficha.conOrdenesTexto': 'Those orders keep the price that was charged. To stop offering it, deactivate it from «More actions».',
  'admin.catalogo.ficha.conOrdenesInactivo': 'Those orders keep the price that was charged. It is already inactive: it is not offered when creating new orders.',
  'admin.catalogo.aviso.guardado': '«{nombre}» was saved',
  'admin.catalogo.aviso.guardadoTexto': 'It can now be added to orders.',
  'admin.catalogo.categoria.titulo': 'New category',
  'admin.catalogo.categoria.descripcion': 'It is saved in the catalog of the workshop.',
  'admin.catalogo.categoria.nombre': 'Category name',
  'admin.catalogo.categoria.guardar': 'Save category',
  'admin.catalogo.categoria.cancelar': 'Cancel',
  'admin.catalogo.desactivar.tituloServicio': 'Deactivate this service?',
  'admin.catalogo.desactivar.tituloProducto': 'Deactivate this product?',
  'admin.catalogo.desactivar.texto':
    '«{nombre}» will no longer appear when creating new orders or when dictating by voice.',
  'admin.catalogo.desactivar.ordenes': 'It is registered in {n} orders.',
  'admin.catalogo.desactivar.unaOrden': 'It is registered in 1 order.',
  'admin.catalogo.desactivar.ordenesNoCambian':
    'Those orders do not change: they keep the service and the price that was charged. Nothing is deleted from history.',
  'admin.catalogo.desactivar.reactivar':
    'You can activate it again whenever you want from the list, with the filter Status: Inactive.',
  'admin.catalogo.desactivar.confirmar': 'Yes, deactivate',
  'admin.catalogo.desactivar.cancelar': 'Cancel',
  'admin.catalogo.confirmar.cargando': 'Loading…',
  'admin.catalogo.confirmar.error': 'Could not read the product or service. Try again.',
  'admin.catalogo.confirmar.cerrar': 'Close',
  'admin.catalogo.eliminar.tituloServicio': 'Delete this service?',
  'admin.catalogo.eliminar.tituloProducto': 'Delete this product?',
  'admin.catalogo.eliminar.texto': '«{nombre}» is removed from the catalog and its code becomes free for another one.',
  'admin.catalogo.eliminar.sinOrdenes': 'It does not appear in any order.',
  'admin.catalogo.eliminar.sinOrdenesTexto': 'That is why it can be deleted without touching the history.',
  'admin.catalogo.eliminar.irreversible': 'This cannot be undone. If you only want to stop offering it, deactivate it.',
  'admin.catalogo.eliminar.confirmar': 'Yes, delete',
  'admin.catalogo.eliminar.cancelar': 'Cancel',
  'admin.catalogo.eliminar.noSePuedeServicio': 'This service cannot be deleted',
  'admin.catalogo.eliminar.noSePuedeProducto': 'This product cannot be deleted',
  'admin.catalogo.eliminar.noSePuedeTexto': '«{nombre}» has been used: only what appears in no order can be deleted.',
  'admin.catalogo.eliminar.desactivarEnSuLugar': 'To stop offering it, deactivate it. Those orders do not change: they keep what was charged.',
  'admin.catalogo.eliminar.yaInactivo': 'It is already inactive: it is not offered when creating new orders.',
  'admin.catalogo.eliminar.desactivar': 'Deactivate',

  // --- Customers (M-5) ---
  'admin.clientes.titulo': 'Customers',
  'admin.clientes.info': 'The people and companies the workshop serves. Tap one to see their record, with their contacts and assets. The search finds them by part of the name or code; type the document, the phone and the email in full.',
  'admin.clientes.nuevo': 'New customer',
  'admin.clientes.nuevo.info': 'With the type and the name it can already be saved; the rest can be filled in later. Fields marked with * are required.',
  'admin.clientes.buscar': 'Search by name, phone or email',
  'admin.clientes.filtro.tipo': 'Type',
  'admin.clientes.filtro.todos': 'All',
  'admin.clientes.filtro.estado': 'Status',
  'admin.clientes.columna.codigo': 'Code',
  'admin.clientes.columna.nombre': 'Name',
  'admin.clientes.columna.tipo': 'Type',
  'admin.clientes.columna.documento': 'Document',
  'admin.clientes.columna.telefono': 'Phone',
  'admin.clientes.columna.correo': 'Email',
  'admin.clientes.columna.vehiculos': 'Vehicles',
  'admin.clientes.columna.estado': 'Status',
  'admin.clientes.persona': 'Person',
  'admin.clientes.empresa': 'Company',
  'admin.clientes.activo': 'Active',
  'admin.clientes.inactivo': 'Inactive',
  'admin.clientes.sinResultados': 'No customer matches “{texto}”.',
  'admin.clientes.sinResultados.conFiltros': {
    one: 'No customer matches “{texto}” and the filter {filtros}.',
    other: 'No customer matches “{texto}” and the filters {filtros}.',
  },
  'admin.clientes.sinResultados.buscar': 'Check the spelling. Documents, phone numbers and emails must be typed in full.',
  'admin.clientes.sinResultados.buscarYCombos': 'Check the spelling or remove the filters to see more results.',
  'admin.clientes.sinResultados.combos': 'No customer matches the selected type or status. Change them or clear the filters.',
  'admin.clientes.sinResultados.pagina': 'There are no customers left on this page. Go back to the start of the list.',
  'admin.clientes.vacio.titulo': 'No customers yet',
  'admin.clientes.vacio.texto': 'Register the people and companies the shop serves so you can open orders for them.',
  'admin.clientes.vacio.nota': 'Lots of them? Import an Excel file with type, name, document, phone and email.',
  'admin.clientes.mas': 'More actions',
  'admin.clientes.menu.importarArchivo': 'Import from file',
  'admin.clientes.exportar.grupo': 'Export',
  'admin.clientes.exportar.aExcel': 'Export to Excel',
  'admin.clientes.exportar.aCsv': 'Export to CSV',
  'admin.clientes.exportar.aPdf': 'Export to PDF',
  'admin.clientes.exportar.nota': 'What the current filter shows is exported.',
  'admin.clientes.menu.editar': 'Edit',
  'admin.clientes.menu.activar': 'Activate',
  'admin.clientes.menu.desactivar': 'Deactivate',
  'admin.clientes.menu.eliminar': 'Delete',
  'admin.clientes.editar.titulo': 'Edit customer',
  'admin.clientes.editar.info': 'Correct the customer\'s details. Fields marked with * are required.',
  'admin.clientes.noExiste.titulo': 'This customer does not exist',
  'admin.clientes.noExiste.texto':
    'It may have been deleted, or the link is wrong. Go back to the list to look for it.',
  'admin.clientes.volver': 'Back to customers',
  'admin.clientes.formulario.general': 'General information',
  'admin.clientes.formulario.tipo': 'Type',
  'admin.clientes.formulario.tipoAyuda': 'Natural person or company. Determines which documents apply.',
  'admin.clientes.formulario.codigo': 'Code',
  'admin.clientes.formulario.codigoAyuda': 'Assigned automatically. You can replace it.',
  'admin.clientes.formulario.nombre': 'Name',
  'admin.clientes.formulario.razonSocial': 'Legal name',
  'admin.clientes.formulario.nombreComercial': 'Trade name',
  'admin.clientes.formulario.nombreComercialAyuda':
    'The name the company is known by, if it is not its legal name. Search finds it too. Optional.',
  'admin.clientes.formulario.nombreComercialEjemplo': 'E.g. El Rayo',
  'admin.clientes.formulario.identificacion': 'Identification',
  'admin.clientes.formulario.identificacionAyuda':
    'The document that identifies the customer. Optional; the same document cannot belong to two customers.',
  'admin.clientes.formulario.tipoDocumento': 'Document type',
  'admin.clientes.formulario.tipoDocumentoAyuda': 'DUI, NIT, NRC depending on the country. Optional.',
  'admin.clientes.formulario.sinDocumento': 'No document',
  'admin.clientes.formulario.numeroDocumento': 'Document number',
  'admin.clientes.formulario.numeroDocumentoAyuda': 'The number exactly as it appears on the document.',
  'admin.clientes.formulario.contacto': 'Contact',
  'admin.clientes.formulario.contactoAyuda': 'How to reach the customer. All are optional.',
  'admin.clientes.formulario.telefono': 'Phone',
  'admin.clientes.formulario.telefonoAyuda': 'Preferably WhatsApp, if available.',
  'admin.clientes.formulario.correo': 'Email address',
  'admin.clientes.formulario.correoAyuda': 'To send quotes and receipts.',
  'admin.clientes.formulario.direccionYNotas': 'Address and notes',
  'admin.clientes.formulario.direccion': 'Address',
  'admin.clientes.formulario.notas': 'Notes',
  'admin.clientes.formulario.estado': 'Status',
  'admin.clientes.formulario.activoAyuda':
    'Available to open orders for. Turn it off if the customer is no longer active.',
  'admin.clientes.formulario.guardarCambios': 'Save changes',
  'admin.clientes.formulario.creado': 'Created on {fecha}',
  'admin.clientes.formulario.modificado': 'Last modified: {fecha}',
  'admin.clientes.formulario.modificadoPor': 'Last modified: {fecha} by {autor}',
  'admin.clientes.formulario.hoy': 'today',
  'admin.clientes.formulario.eliminar': 'Delete customer',
  'admin.clientes.contacto.ayuda': 'Customer phones and emails. Mark one as main for each type.',
  'admin.clientes.contacto.tipo': 'Type',
  'admin.clientes.contacto.valor': 'Value',
  'admin.clientes.contacto.etiqueta': 'Label',
  'admin.clientes.contacto.etiquetaEjemplo': 'E.g. Office',
  'admin.clientes.contacto.principal': 'Main',
  'admin.clientes.contacto.telefono': 'Phone',
  'admin.clientes.contacto.correo': 'Email',
  'admin.clientes.contacto.agregar': 'Add contact',
  'admin.clientes.contacto.tipoDe': 'Type of contact {n}',
  'admin.clientes.contacto.valorDe': 'Value of contact {n}',
  'admin.clientes.contacto.etiquetaDe': 'Label of contact {n}',
  'admin.clientes.contacto.principalDe': 'Contact {n} is main',
  'admin.clientes.contacto.quitar': 'Remove contact {n}',
  'admin.clientes.error.titulo': 'Could not complete',
  'admin.clientes.error.repetido': '{tipo} {numero} is already registered to {nombre} ({codigo}).',
  'admin.clientes.error.verExistente': 'View existing customer →',
  'admin.clientes.eliminar.titulo': 'Delete customer',
  'admin.clientes.eliminar.texto':
    'You are about to delete {nombre} ({codigo}). They will no longer appear in lists and searches.',
  'admin.clientes.eliminar.boton': 'Delete customer',
  'admin.clientes.eliminar.bloqueado.titulo': 'Cannot be deleted',
  'admin.clientes.eliminar.bloqueado.activo':
    '{nombre} has {historial}. Deleting is only for a customer registered by mistake. If they are no longer your customer, deactivate them: their orders and assets are kept and no new orders are opened for them.',
  'admin.clientes.eliminar.bloqueado.inactivo':
    '{nombre} has {historial}. Deleting is only for a customer registered by mistake. They are already inactive: their orders and assets are kept and no new orders are opened for them.',
  'admin.clientes.eliminar.bloqueado.ordenes': { one: '1 order', other: '{n} orders' },
  'admin.clientes.eliminar.bloqueado.activos': { one: '1 asset', other: '{n} assets' },
  'admin.clientes.eliminar.desactivar': 'Deactivate customer',
  'admin.clientes.eliminar.sinGuardar.titulo': 'You have unsaved changes',
  'admin.clientes.eliminar.sinGuardar.texto':
    'Do you want to save them before deactivating {nombre}? Otherwise they are discarded.',
  'admin.clientes.eliminar.sinGuardar.descartar': 'Discard and deactivate',
  'admin.clientes.eliminar.sinGuardar.guardar': 'Save and deactivate',
  'admin.clientes.eliminar.desactivarConflictoEdicion':
    'Someone else changed this customer in the meantime. The latest version loads when you close: review it and try again.',
  'admin.clientes.eliminar.desactivarConflicto':
    'Someone else changed this customer in the meantime. The latest version is loaded: try again.',
  'admin.clientes.eliminar.entendido': 'Got it',
  'admin.clientes.aviso.creado': 'Customer {nombre} was created',
  'admin.clientes.aviso.guardado': 'Changes to {nombre} were saved',
  'admin.clientes.aviso.eliminado': 'Customer {nombre} was deleted',
  'admin.clientes.aviso.activado': '{nombre} is active again',
  'admin.clientes.aviso.desactivado': '{nombre} is now inactive',
  'admin.clientes.aviso.guardadoYDesactivado': 'Changes to {nombre} were saved and they are now inactive',
  'admin.clientes.menu.verFicha': 'View details',

  'admin.clientes.importar.titulo': 'Import customers',
  'admin.clientes.importar.texto':
    'Upload a file with one row per customer. The columns are the ones in the template: code, type, name, trade name, document, phone, email, address and notes.',
  'admin.clientes.importar.plantilla': 'Download template:',
  'admin.clientes.importar.plantillaExcel': 'Excel',
  'admin.clientes.importar.plantillaCsv': 'CSV',
  'admin.clientes.importar.zona': 'Drag the file here or',
  'admin.clientes.importar.elegir': 'choose it',
  'admin.clientes.importar.cambiar': 'Change',
  'admin.clientes.importar.formatos': 'Excel (.xlsx) or CSV, up to 5 MB',
  'admin.clientes.importar.boton': 'Import',
  'admin.clientes.importar.error.titulo': 'Could not import',
  'admin.clientes.importar.cortada':
    'The import stopped halfway and part of it may already be saved. Check the list before trying again: importing the file again would repeat the customers without code or document.',
  'admin.clientes.importar.revisar.titulo': 'Review import',
  'admin.clientes.importar.revisar.texto': {
    one: '1 row was read from {archivo}. Rows with errors are not imported: fix them in the file and upload it again, or import only the valid ones.',
    other:
      '{n} rows were read from {archivo}. Rows with errors are not imported: fix them in the file and upload it again, or import only the valid ones.',
  },
  'admin.clientes.importar.revisar.listos': {
    one: '1 customer ready',
    other: '{n} customers ready',
  },
  'admin.clientes.importar.revisar.conErrores': {
    one: '1 row with errors',
    other: '{n} rows with errors',
  },
  'admin.clientes.importar.revisar.advertencia':
    'If you import the valid ones now, upload only the corrected rows afterwards: the whole file would repeat the customers with no code or document.',
  'admin.clientes.importar.revisar.boton': {
    one: 'Import the valid one',
    other: 'Import the {n} valid ones',
  },
  'admin.clientes.importar.hecho.titulo': {
    one: '1 customer imported',
    other: '{n} customers imported',
  },
  'admin.clientes.importar.hecho.texto': {
    one: 'This row did not get in. Fix it and import it alone: uploading the whole file again would repeat the customers without code or document.',
    other:
      'These {n} rows did not get in. Fix them and import them alone: uploading the whole file again would repeat the customers without code or document.',
  },
  'admin.clientes.importar.entendido': 'Got it',
  'admin.clientes.importar.aviso': {
    one: '1 customer imported',
    other: '{n} customers imported',
  },
  'admin.clientes.importar.columnaFila': 'Row',
  'admin.clientes.importar.columnaNombre': 'Name',
  'admin.clientes.importar.columnaProblema': 'Problem',
  'admin.clientes.importar.columnaTipoDocumento': 'Document type',
  'admin.clientes.importar.columnaDireccion': 'Address',
  'admin.clientes.importar.columnaNotas': 'Notes',
  'admin.clientes.importar.motivo': '{columna}: {texto}',
  'admin.clientes.importar.motivoConValor': '{columna}: {texto} ({valor})',

  'admin.clientes.ficha.migas': 'Breadcrumb',
  'admin.clientes.ficha.info': 'The customer\'s details, assets and latest orders. To correct something, use «Edit customer».',
  'admin.clientes.ficha.desde': 'Customer since {fecha}',
  'admin.clientes.historial.info':
    'Who registered the customer, who changed what and when an asset was added or removed, newest first. Personal details —name, document, notes, a phone or an email— show that they changed, but not to what: they are not stored.',
  'admin.clientes.ficha.editar': 'Edit customer',
  'admin.clientes.ficha.contacto.titulo': 'Contact',
  'admin.clientes.ficha.contacto.texto': 'The customer’s contact details. The primary one comes first.',
  'admin.clientes.ficha.contacto.telefonoPrincipal': 'Primary phone',
  'admin.clientes.ficha.contacto.otrosTelefonos': 'Other phones',
  'admin.clientes.ficha.contacto.correoPrincipal': 'Primary email',
  'admin.clientes.ficha.contacto.otrosCorreos': 'Other emails',
  'admin.clientes.ficha.contacto.direccion': 'Address',
  'admin.clientes.ficha.contacto.vacio': 'This customer has no contact details yet.',
  'admin.clientes.ficha.activos.vehiculos': 'Vehicles ({n})',
  'admin.clientes.ficha.activos.equipos': 'Equipment ({n})',
  'admin.clientes.ficha.activos.activos': 'Assets ({n})',
  'admin.clientes.ficha.activos.texto':
    'Assets registered to this customer. Orders are opened on them.',
  'admin.clientes.ficha.activos.agregar.vehiculos': 'Add vehicle',
  'admin.clientes.ficha.activos.agregar.equipos': 'Add equipment',
  'admin.clientes.ficha.activos.agregar.activos': 'Add asset',
  'admin.clientes.ficha.activos.vacio.vehiculos': 'This customer has no vehicles yet',
  'admin.clientes.ficha.activos.vacio.equipos': 'This customer has no equipment yet',
  'admin.clientes.ficha.activos.vacio.activos': 'This customer has no assets yet',
  'admin.clientes.ficha.activos.vacioTexto': 'Add the first one to start opening orders.',
  'admin.clientes.ficha.activos.columna.codigo': 'Code',
  'admin.clientes.ficha.activos.columna.marcaModelo': 'Make / Model',
  'admin.clientes.ficha.activos.columna.tipo': 'Type',
  'admin.clientes.ficha.activos.columna.identificador': 'Identifier',
  'admin.clientes.ficha.activos.columna.anio': 'Year',
  'admin.clientes.ficha.activos.columna.ordenes': 'Orders',
  'admin.clientes.ficha.activos.columna.estado': 'Status',
  'admin.clientes.ficha.ordenes.titulo': 'Latest orders',
  'admin.clientes.ficha.ordenes.texto': 'The customer’s most recent orders, from any location.',
  'admin.clientes.ficha.ordenes.vacio': 'No orders for this customer yet',
  'admin.clientes.ficha.ordenes.vacioTexto': 'They’ll show up here once the first one is opened.',
  'admin.clientes.ficha.ordenes.sinFolio': 'No number',
  'admin.clientes.ficha.ordenes.columna.folio': 'Number',
  'admin.clientes.ficha.ordenes.columna.vehiculos': 'Vehicle',
  'admin.clientes.ficha.ordenes.columna.equipos': 'Equipment',
  'admin.clientes.ficha.ordenes.columna.activos': 'Asset',
  'admin.clientes.ficha.ordenes.columna.sucursal': 'Location',
  'admin.clientes.ficha.ordenes.columna.estado': 'Status',
  'admin.clientes.ficha.ordenes.columna.abierta': 'Opened',
  'admin.clientes.ficha.ordenes.columna.total': 'Total',
  'admin.clientes.ficha.notas': 'Notes',
  'error.cliente.nombre_persona': 'Type the person’s name',
  'error.cliente.nombre_empresa': 'Type the company’s legal name',
  'error.catalogo.nombre_servicio': 'Enter the service name',
  'error.catalogo.nombre_producto': 'Enter the product name',
  'error.catalogo.numero_invalido': 'Enter a number, like 45.50',

  'admin.marcas.titulo': 'Brands and models',
  'admin.marcas.info': 'The brands and models chosen when registering an asset. Choose a brand to see its models. Nothing is deleted: what is no longer used is deactivated and stops being offered.',
  'admin.marcas.columnaMarcas': 'Brands',
  'admin.marcas.columnaModelos': 'Models',
  'admin.marcas.columnaModelosDe': 'Models of {marca}',
  'admin.marcas.nuevaMarca': 'New brand',
  'admin.marcas.nuevoModelo': 'New model',
  'admin.marcas.buscar': 'Search brand',
  'admin.marcas.filtro.tipo': 'Type',
  'admin.marcas.filtro.todos': 'All',
  'admin.marcas.filtro.estado': 'Status',
  'admin.marcas.filtro.todas': 'All',
  'admin.marcas.activa': 'Active',
  'admin.marcas.inactiva': 'Inactive',
  'admin.marcas.inactivo': 'Inactive',
  'admin.marcas.conteoModelos': { one: '1 model', other: '{n} models' },
  'admin.marcas.vacio.titulo': 'No brands yet',
  'admin.marcas.vacio.texto':
    'Brands and their models are what you pick when registering a vehicle or a device. Add the ones you service most; the rest can be created while registering the asset.',
  'admin.marcas.eligeUnaMarca': 'Pick a brand to see its models.',
  'admin.marcas.sinModelos': '{marca} has no models yet.',
  'admin.marcas.menu.renombrar': 'Rename',
  'admin.marcas.menu.desactivar': 'Deactivate',
  'admin.marcas.menu.activar': 'Activate',
  'admin.marcas.nombre': 'Name',
  'admin.marcas.guardar': 'Save',
  'admin.marcas.tipo': 'Asset type',
  'admin.marcas.tipo.generica': 'All types',
  'admin.marcas.tipo.ayuda':
    'If the brand only makes one type of asset, pick it: that way it does not show up when registering the others. It cannot be changed later.',
  'admin.marcas.ventanaMarca.nueva': 'New brand',
  'admin.marcas.ventanaMarca.nuevaTexto': "It is saved in the workshop's catalog.",
  'admin.marcas.ventanaMarca.renombrar': 'Rename brand',
  'admin.marcas.ventanaMarca.renombrarTexto': 'Assets that already have it show the new name.',
  'admin.marcas.ventanaMarca.ejemplo': 'E.g. Toyota',
  'admin.marcas.ventanaMarca.crear': 'Save brand',
  'admin.marcas.parecida.deTipo':
    '«{nombre}» already exists for {tipo}. If it is the same brand, there is no need to create another.',
  'admin.marcas.parecida.generica':
    '«{nombre}» already exists for all types. If it is the same brand, there is no need to create another.',
  'admin.marcas.ventanaModelo.nuevo': 'New model',
  'admin.marcas.ventanaModelo.nuevoTexto': 'It is added to the models of {marca}.',
  'admin.marcas.ventanaModelo.renombrar': 'Rename model',
  'admin.marcas.ventanaModelo.ejemplo': 'E.g. Hilux',
  'admin.marcas.ventanaModelo.crear': 'Save model',
  'admin.marcas.aviso.marcaCreada': 'Brand {nombre} was created',
  'admin.marcas.aviso.marcaRenombrada': 'The brand is now called {nombre}',
  'admin.marcas.aviso.marcaDesactivada': '{nombre} is no longer offered when registering assets',
  'admin.marcas.aviso.marcaActivada': '{nombre} is offered again',
  'admin.marcas.aviso.modeloCreado': 'Model {nombre} was created',
  'admin.marcas.aviso.modeloRenombrado': 'The model is now called {nombre}',
  'admin.marcas.aviso.modeloDesactivado': '{nombre} is no longer offered when registering assets',
  'admin.marcas.aviso.modeloActivado': '{nombre} is offered again',
  'admin.marcas.error.titulo': 'Could not complete',
  'admin.estados.titulo': 'Order statuses',
  'admin.estados.info':
    'The steps an order goes through, grouped by stage. Each workshop names them as it likes; the stage is what the system understands: delivering asks for a customer and an order number, cancelling asks for a reason. Nothing is deleted: what is no longer used is deactivated, and the orders in it stay there.',
  'admin.estados.etapa.open': 'Open',
  'admin.estados.etapa.inProgress': 'In progress',
  'admin.estados.etapa.completed': 'Completed',
  'admin.estados.etapa.closed': 'Delivered',
  'admin.estados.etapa.cancelled': 'Cancelled',
  'admin.estados.etapa.open.info':
    'Received and not yet worked on. The initial status, the one every order starts in, belongs to this stage.',
  'admin.estados.etapa.inProgress.info': 'Work under way: diagnosis, approved, in progress.',
  'admin.estados.etapa.completed.info':
    'The work is done and delivery is pending. Here you decide which statuses freeze the amounts: once an order reaches one, its prices no longer change.',
  'admin.estados.etapa.closed.info':
    'Delivered, nothing pending. Delivering asks for a customer and an order number. Always freezes the amounts.',
  'admin.estados.etapa.cancelled.info': 'The order did not go ahead. Cancelling asks for a reason. Always freezes the amounts.',
  'admin.estados.etapaVacia': 'This stage has no statuses.',
  'admin.estados.agregar': 'Add status',
  'admin.estados.inicial': 'Initial',
  'admin.estados.congela': 'Freezes amounts',
  'admin.estados.inactivo': 'Inactive',
  'admin.estados.conteoOrdenes': { one: '1 order', other: '{n} orders' },
  'admin.estados.subir': 'Move {nombre} up',
  'admin.estados.bajar': 'Move {nombre} down',
  'admin.estados.menu.editar': 'Edit',
  'admin.estados.menu.inicial': 'Make initial',
  'admin.estados.menu.desactivar': 'Deactivate',
  'admin.estados.menu.activar': 'Activate',
  'admin.estados.ventana.nuevo': 'New status',
  'admin.estados.ventana.nuevoTexto': 'It starts active, at the end of its stage.',
  'admin.estados.ventana.editar': 'Edit status',
  'admin.estados.ventana.editarTexto':
    'Orders already in this status show the changes. If you change its stage, it moves to the end of the new one.',
  'admin.estados.campo.nombre': 'Name',
  'admin.estados.campo.ejemplo': 'E.g. Waiting for parts',
  'admin.estados.campo.etapa': 'Stage',
  'admin.estados.campo.etapa.info':
    'What the system understands about the status: whether the order is about to start, in progress, completed, delivered or cancelled.',
  'admin.estados.campo.etapaFija': 'Stage: {etapa}',
  'admin.estados.campo.etapaFija.info': {
    one: 'It has 1 order: its stage and whether it freezes the amounts cannot change. Move it to another status, or create a new one in the stage you want.',
    other:
      'It has {n} orders: its stage and whether it freezes the amounts cannot change. Move them to another status, or create a new one in the stage you want.',
  },
  'admin.estados.campo.color': 'Color',
  'admin.estados.campo.congela': 'Freezes the amounts',
  'admin.estados.campo.congela.info':
    'Once an order reaches this status, the prices of its lines and its total no longer change.',
  'admin.estados.color.sin': 'No color',
  'admin.estados.color.gris': 'Grey',
  'admin.estados.color.azul': 'Blue',
  'admin.estados.color.celeste': 'Cyan',
  'admin.estados.color.verde': 'Green',
  'admin.estados.color.amarillo': 'Yellow',
  'admin.estados.color.naranja': 'Orange',
  'admin.estados.color.rojo': 'Red',
  'admin.estados.color.morado': 'Purple',
  'admin.estados.guardar': 'Save',
  'admin.estados.crear': 'Save status',
  'admin.estados.aviso.creado': 'Status {nombre} was created',
  'admin.estados.aviso.editado': '{nombre} was saved',
  'admin.estados.aviso.inicial': 'New orders now start in {nombre}',
  'admin.estados.aviso.desactivado': '{nombre} is no longer offered for moving orders',
  'admin.estados.aviso.activado': '{nombre} is offered again',
  'admin.estados.aviso.reordenado': 'The order was saved',
  'admin.estados.error.titulo': 'Could not complete',
  // --- Catalogs (#154) ---
  'admin.catalogos.titulo': 'Catalogs',
  'admin.catalogos.info':
    'The lists chosen in the forms: product and service categories, staff specialties, and asset types with their fields. What is already in use is not deleted: it is deactivated and stops being offered.',
  'admin.catalogos.lista.titulo': 'Catalogs',
  'admin.catalogos.lista.texto': 'Choose one to see its items',
  'admin.catalogos.sinPermiso': 'Your role cannot maintain any of these catalogs.',
  'admin.catalogos.error.titulo': 'The change could not be made',
  'admin.catalogos.buscar': 'Search by name',
  'admin.catalogos.filtro.estado': 'Status',
  'admin.catalogos.filtro.todos': 'All',
  'admin.catalogos.filtro.activos': 'Active',
  'admin.catalogos.filtro.inactivos': 'Inactive',
  'admin.catalogos.inactiva': 'Inactive',
  'admin.catalogos.inactivo': 'Inactive',
  'admin.catalogos.inactivas': { one: '1 inactive', other: '{n} inactive' },
  'admin.catalogos.inactivos': { one: '1 inactive', other: '{n} inactive' },
  'admin.catalogos.sinUsar': 'Unused',
  'admin.catalogos.nombre': 'Name',
  'admin.catalogos.guardarCambios': 'Save changes',
  'admin.catalogos.eliminar': 'Delete',
  'admin.catalogos.menu.editar': 'Edit',
  'admin.catalogos.menu.campos': 'Fields',
  'admin.catalogos.menu.desactivar': 'Deactivate',
  'admin.catalogos.menu.activar': 'Activate',
  'admin.catalogos.menu.eliminar': 'Delete',
  'admin.catalogos.categorias.titulo': 'Product and service categories',
  'admin.catalogos.categorias.conteo': { one: '1 category', other: '{n} categories' },
  'admin.catalogos.categorias.usada': { one: 'Used in 1', other: 'Used in {n}' },
  'admin.catalogos.categorias.nueva': 'New category',
  'admin.catalogos.categorias.editar': 'Edit category',
  'admin.catalogos.categorias.ventanaInfo':
    'Groups products and services in the filter and in the Products/Services dropdown.',
  'admin.catalogos.categorias.ejemplo': 'E.g.: Brakes',
  'admin.catalogos.categorias.vacio': 'There are no categories yet',
  'admin.catalogos.categorias.aviso.creada': 'Category {nombre} created',
  'admin.catalogos.categorias.aviso.guardada': 'Category {nombre} saved',
  'admin.catalogos.categorias.aviso.desactivada': 'Category {nombre} deactivated',
  'admin.catalogos.categorias.aviso.activada': 'Category {nombre} activated',
  'admin.catalogos.categorias.aviso.eliminada': 'Category {nombre} deleted',
  'admin.catalogos.categorias.eliminar.titulo': 'Delete the category {nombre}?',
  'admin.catalogos.categorias.eliminar.texto':
    'No product or service uses it, so nothing is lost. Its name becomes free to use again.',
  'admin.catalogos.especialidades.titulo': 'Specialties',
  'admin.catalogos.especialidades.conteo': { one: '1 specialty', other: '{n} specialties' },
  'admin.catalogos.especialidades.usada': { one: '1 person has it', other: '{n} people have it' },
  'admin.catalogos.especialidades.nueva': 'New specialty',
  'admin.catalogos.especialidades.editar': 'Edit specialty',
  'admin.catalogos.especialidades.ventanaInfo':
    'What each person in the workshop knows how to do. Assigned in their record; it is also created when typed there.',
  'admin.catalogos.especialidades.ejemplo': 'E.g.: Suspension',
  'admin.catalogos.especialidades.vacio': 'There are no specialties yet',
  'admin.catalogos.especialidades.aviso.creada': 'Specialty {nombre} created',
  'admin.catalogos.especialidades.aviso.guardada': 'Specialty {nombre} saved',
  'admin.catalogos.especialidades.aviso.desactivada': 'Specialty {nombre} deactivated',
  'admin.catalogos.especialidades.aviso.activada': 'Specialty {nombre} activated',
  'admin.catalogos.especialidades.aviso.eliminada': 'Specialty {nombre} deleted',
  'admin.catalogos.especialidades.eliminar.titulo': 'Delete the specialty {nombre}?',
  'admin.catalogos.especialidades.eliminar.texto':
    'No staff record has it, so nothing is lost. Its name becomes free to use again.',
  'admin.catalogos.tipos.titulo': 'Asset types',
  'admin.catalogos.tipos.conteo': { one: '1 type', other: '{n} types' },
  'admin.catalogos.tipos.usado': { one: 'Used in 1', other: 'Used in {n}' },
  'admin.catalogos.tipos.vacio': 'There are no asset types yet',
  'admin.catalogos.tipos.camposDe': 'Fields of {tipo}',

  'admin.tipos.nuevo': 'New type',
  'admin.tipos.menu.editarCampo': 'Edit',
  'admin.tipos.menu.apagar': 'Turn off',
  'admin.tipos.menu.encender': 'Turn on',
  'admin.tipos.resumen.exige': 'Identified by «{identificador}», which is required.',
  'admin.tipos.resumen.opcional': 'Identified by «{identificador}», which is optional.',
  'admin.tipos.campos.titulo': 'Fields',
  'admin.tipos.campos.texto':
    'What the form asks for besides brand, model, year and identifier, in this order.',
  'admin.tipos.campos.vacio':
    'This type has no fields yet: the form will only ask for brand, model, year and identifier.',
  'admin.tipos.agregarCampo': 'Add field',
  'admin.tipos.obligatorio': 'Required',
  'admin.tipos.apagado': 'Off',
  'admin.tipos.conteoOpciones': { one: '1 option', other: '{n} options' },
  'admin.tipos.subir': 'Move {nombre} up',
  'admin.tipos.bajar': 'Move {nombre} down',
  'admin.tipos.guardar': 'Save',
  'admin.tipos.nombre': 'Name',
  'admin.tipos.nombreEjemplo': 'E.g. Compressor',
  'admin.tipos.identificador': 'How it is identified',
  'admin.tipos.identificadorEjemplo': 'E.g. Serial number',
  'admin.tipos.identificadorAyuda': 'The name of the data that tells each one apart: «Plate», «Serial number», «Ticket no.».',
  'admin.tipos.exigeIdentificador': 'Require the identifier',
  'admin.tipos.exigeIdentificadorAyuda':
    'If you turn it on, an asset of this type cannot be registered without it. Existing ones do not change.',
  'admin.tipos.ventanaTipo.nuevoTexto': 'Then you add the fields it needs.',
  'admin.tipos.ventanaTipo.editar': 'Edit asset type',
  'admin.tipos.ventanaTipo.editarTexto': 'Assets of this type show the new names.',
  'admin.tipos.ventanaTipo.crear': 'Create type',
  'admin.tipos.ventanaCampo.nuevo': 'Add field',
  'admin.tipos.ventanaCampo.editar': 'Edit field',
  'admin.tipos.ventanaCampo.texto': 'A piece of data asked for when registering an asset of type {tipo}.',
  'admin.tipos.ventanaCampo.crear': 'Add field',
  'admin.tipos.campo.etiqueta': 'Field name',
  'admin.tipos.campo.etiquetaEjemplo': 'E.g. Mileage',
  'admin.tipos.campo.dato': 'Data type',
  'admin.tipos.campo.datoAyuda': 'It cannot be changed later: assets store the data with this type.',
  'admin.tipos.campo.datoFijo': 'Data type: {dato}. It does not change once created.',
  'admin.tipos.campo.obligatorio': 'Required',
  'admin.tipos.campo.obligatorioAyuda':
    'Use it sparingly: every required field is one more step for whoever registers the asset standing up.',
  'admin.tipos.dato.text': 'Text',
  'admin.tipos.dato.number': 'Number',
  'admin.tipos.dato.bool': 'Yes / No',
  'admin.tipos.dato.date': 'Date',
  'admin.tipos.dato.list': 'List of options',
  'admin.tipos.opciones.titulo': 'Options',
  'admin.tipos.opciones.nombre': 'Option {n}',
  'admin.tipos.opciones.quitar': 'Remove {nombre}',
  'admin.tipos.opciones.nueva': 'New option',
  'admin.tipos.opciones.placeholder': 'Type an option and press Enter',
  'admin.tipos.opciones.agregar': 'Add',
  'admin.tipos.opciones.ayuda': 'The ones that can be picked. You can rename them later.',
  'admin.tipos.opciones.repetida': '«{nombre}» is already on the list.',
  'admin.tipos.opciones.quitadas':
    'Assets that already have {nombres} keep it until someone picks another option.',
  'admin.tipos.aviso.creado': 'Type {nombre} was created',
  'admin.tipos.aviso.guardado': 'Changes to {nombre} were saved',
  'admin.tipos.aviso.activado': '{nombre} is offered again',
  'admin.tipos.aviso.desactivado': '{nombre} is no longer offered for new assets',
  'admin.tipos.aviso.campoAgregado': 'Field {nombre} was added',
  'admin.tipos.aviso.campoGuardado': 'Field {nombre} was saved',
  'admin.tipos.aviso.campoApagado': '{nombre} is no longer asked for; what assets had is kept',
  'admin.tipos.aviso.campoEncendido': '{nombre} is asked for again',
  'admin.tipos.aviso.reordenado': 'The field order was saved',
  'admin.tipos.error.titulo': 'Could not complete',
  'admin.adjuntos.titulo': 'Photos and files',
  'admin.adjuntos.agregar': 'Add',
  'admin.adjuntos.subiendo': {
    one: 'Uploading 1 file…',
    other: 'Uploading {n} files…',
  },
  'admin.adjuntos.cargando': 'Loading…',
  'admin.adjuntos.vacio': 'No photos or files yet.',
  'admin.adjuntos.errorLista': 'Attachments could not be loaded. Please try again in a moment.',
  'admin.adjuntos.tamano': '{kb} KB',
  'admin.adjuntos.eliminar': 'Delete {nombre}',
  'admin.adjuntos.aviso.eliminado': '{nombre} was deleted',
  'admin.adjuntos.error.titulo': 'Could not complete',
  'admin.adjuntos.cuantas': {
    one: '{n} photo',
    other: '{n} photos',
  },

  // --- Expenses (#143) ---
  'admin.gastos.titulo': 'Expenses for this order',
  'admin.gastos.info':
    'What was bought for this job: parts, materials, an outside service. Supplier, invoice and amount can be filled in later.',
  'admin.gastos.registrar': 'Record expense',
  'admin.gastos.vacio': 'No expenses on this order yet',
  'admin.gastos.vacioTexto': 'Record what was bought for this job, with a photo of the invoice.',
  'admin.gastos.noSePudo': 'Could not change the expense',
  'admin.gastos.hoy': 'today',
  'admin.gastos.factura': 'Inv. {numero}',
  'admin.gastos.registro': 'recorded by {persona}',
  'admin.gastos.pendiente': 'Payment pending',
  'admin.gastos.pagado': 'Paid {fecha}',
  'admin.gastos.sinMonto': 'No amount',
  'admin.gastos.acciones': 'Actions for {gasto}',
  'admin.gastos.editar': 'Edit',
  'admin.gastos.cambiarMonto': 'Change amount',
  'admin.gastos.marcarPagado': 'Mark as paid',
  'admin.gastos.quitarPago': 'Unmark as paid',
  'admin.gastos.eliminar': 'Delete',
  'admin.gastos.formulario.nuevo': 'Record expense',
  'admin.gastos.formulario.editar': 'Edit expense',
  'admin.gastos.formulario.info':
    'A description and a date are enough. Whoever handles the cash can fill in the supplier, invoice and amount later.',
  'admin.gastos.formulario.registradoPor': 'Recorded by {persona}.',
  'admin.gastos.formulario.descripcion': 'Description',
  'admin.gastos.formulario.descripcionEjemplo': 'Brake pads and fluid',
  'admin.gastos.formulario.fecha': 'Purchase date',
  'admin.gastos.formulario.proveedor': 'Supplier',
  'admin.gastos.formulario.sinProveedor': 'No supplier',
  'admin.gastos.formulario.nuevoProveedor': 'New supplier',
  'admin.gastos.formulario.factura': 'Invoice no.',
  'admin.gastos.formulario.monto': 'Amount',
  'admin.gastos.formulario.montoInfo': 'What it cost, as the invoice says. Leave it empty for no amount.',
  'admin.gastos.formulario.notas': 'Notes',
  'admin.gastos.comprobante.titulo': 'Receipt',
  'admin.gastos.comprobante.ninguno': 'No photo yet.',
  'admin.gastos.comprobante.quitar': 'Remove {nombre}',
  'admin.gastos.alta.incompleto': 'The expense was recorded, but not all of it',
  'admin.gastos.alta.montoDespues': 'Change it from “Change amount”, in the expense’s “•••” menu.',
  'admin.gastos.alta.cerrar': 'Close',
  'admin.gastos.pago.fecha': 'Payment date',
  'admin.gastos.pago.info': 'The day it was paid. It cannot be before the purchase.',
  'admin.gastos.eliminar.titulo': 'Delete expense',
  'admin.gastos.eliminar.texto':
    'You are about to delete “{gasto}”. It is hidden along with its receipt, and its invoice number becomes free for another expense.',
  'admin.gastos.aviso.registrado': 'Expense recorded',
  'admin.gastos.aviso.guardado': 'Changes saved',
  'admin.gastos.aviso.pagado': 'Expense marked as paid',
  'admin.gastos.aviso.pagoQuitado': 'The expense is pending payment again',
  'admin.gastos.aviso.eliminado': 'Expense deleted',
  'admin.gastos.ficha.info':
    'What was bought, from whom and whether it was paid, with its receipt and who changed it. To fix something, use «Edit expense».',
  'admin.gastos.ficha.compra': 'Bought {fecha}',
  'admin.gastos.ficha.datos': 'Expense details',
  'admin.gastos.ficha.datosInfo':
    'The amount, the supplier and the invoice are filled in by whoever handles the cash. While the amount or the supplier is missing, the expense shows under «To complete» in the expense list.',
  'admin.gastos.ficha.sucursal': 'Branch',
  'admin.gastos.ficha.comprobanteInfo': 'The photo of the invoice or receipt, to check the amount without looking for the paper.',
  'admin.gastos.mas': 'More actions',
  'admin.gastos.verFicha': 'View details',
  'admin.gastos.noExiste.titulo': 'This expense does not exist',
  'admin.gastos.noExiste.texto': 'It may have been deleted. Go back to the order or to the expense list.',
  'admin.gastos.volver': 'Go back',
  'admin.gastos.historial.info':
    'Who registered the expense, who changed what, when it was paid and when the receipt was added or removed, newest first. You only see the amounts if you can view amounts; otherwise it says the amount changed without the figures.',
  'admin.gastos.historial.pagadoEl': 'Paid on {fecha}',
  'admin.gastos.historial.estabaPagado': 'Had been paid on {fecha}',
  'admin.gastos.historial.comprobanteAgregado': 'Receipt added',
  'admin.gastos.historial.comprobanteEliminado': 'Receipt deleted',
  'admin.gastos.listado.titulo': 'Expenses',
  'admin.gastos.listado.info':
    'What is left to pay and what is left to fill in —the amount or the supplier— for this branch, from orders and from the workshop. Tap an expense to see it and complete it.',
  'admin.gastos.listado.sinSucursal': 'Pick a branch above to see its expenses.',
  'admin.gastos.listado.filtro': 'Show',
  'admin.gastos.listado.porPagar': 'Payment pending',
  'admin.gastos.listado.porCompletar': 'To complete',
  'admin.gastos.listado.todos': 'All',
  'admin.gastos.listado.columna.gasto': 'Expense',
  'admin.gastos.listado.columna.orden': 'Order',
  'admin.gastos.listado.columna.compra': 'Purchase',
  'admin.gastos.listado.columna.pago': 'Payment',
  'admin.gastos.listado.delTaller': 'Workshop',
  'admin.gastos.listado.falta': 'Missing: {que}',
  'admin.gastos.listado.falta.monto': 'the amount',
  'admin.gastos.listado.falta.proveedor': 'the supplier',
  'admin.gastos.listado.vacio.por-pagar': 'No expenses pending payment',
  'admin.gastos.listado.vacio.por-completar': 'No expenses to complete',
  'admin.gastos.listado.vacio.todos': 'No expenses in this branch yet',
  'admin.gastos.listado.paginaVacia': 'What was on this page has been completed or paid.',
  'admin.gastos.listado.vacio.texto':
    'Expenses are recorded from an order, or here if they belong to the workshop: rent, electricity.',
  'admin.activos.titulo': 'Assets',
  'admin.activos.info': 'What customers bring to the workshop: their cars, their equipment. Orders are opened on an asset.',
  'admin.activos.nuevo': 'New asset',
  'admin.activos.nuevo.descripcion':
    'Pick the type and its fields show up. Only what is marked with * is required: the rest can be filled in later.',
  'admin.activos.editar.titulo': 'Edit asset',
  'admin.activos.editar.info': 'Correct the asset\'s details. Fields marked with * are required.',
  'admin.activos.buscar': 'Search by plate, serial number or code',
  'admin.activos.filtro.tipo': 'Type',
  'admin.activos.filtro.estado': 'Status',
  'admin.activos.filtro.todos': 'All',
  'admin.activos.activo': 'Active',
  'admin.activos.inactivo': 'Inactive',
  'admin.activos.columna.codigo': 'Code',
  'admin.activos.columna.identificador': 'Identifier',
  'admin.activos.columna.tipo': 'Type',
  'admin.activos.columna.marcaModelo': 'Brand / Model',
  'admin.activos.columna.anio': 'Year',
  'admin.activos.columna.cliente': 'Customer',
  'admin.activos.columna.estado': 'Status',
  'admin.activos.vacio.titulo': 'No assets yet',
  'admin.activos.vacio.texto':
    'An asset is what the customer brings to the workshop: their car, their device. Orders are opened on it.',
  'admin.activos.sinResultados': 'No asset matches «{texto}».',
  'admin.activos.menu.verFicha': 'View details',
  'admin.activos.menu.editar': 'Edit',
  'admin.activos.menu.activar': 'Activate',
  'admin.activos.menu.desactivar': 'Deactivate',
  'admin.activos.menu.eliminar': 'Delete',
  'admin.activos.noExiste.titulo': 'This asset does not exist',
  'admin.activos.noExiste.texto': "It may have been deleted. Go back to the list to see the workshop's assets.",
  'admin.activos.volver': 'Back to assets',
  'admin.activos.guardarCambios': 'Save changes',
  'admin.activos.ficha.info':
    'The asset, who it belongs to and what its type asks for. To correct something, use «Edit asset».',
  'admin.activos.ficha.sinCliente': 'No customer',
  'admin.activos.mas': 'More actions',
  'admin.activos.historial.info':
    'Who registered the asset, who changed what and who added or deleted photos and files, newest first. The plate, the code, the notes and the text details show that they changed, but not to what: they are not stored.',
  'admin.activos.seccion.general': 'General information',
  'admin.activos.seccion.generalTexto': 'What it is and how it is recognized.',
  'admin.activos.seccion.marca': 'Brand and model',
  'admin.activos.seccion.marcaTexto': 'If it is not on the list, create it right here.',
  'admin.activos.seccion.cliente': 'Customer',
  'admin.activos.seccion.clienteTexto': 'Who owns it. Optional: it can be assigned later.',
  'admin.activos.seccion.detalles': 'Details',
  'admin.activos.seccion.detallesTexto': 'The data the {tipo} type asks for.',
  'admin.activos.seccion.notas': 'Notes',
  'admin.activos.tipo': 'Asset type',
  'admin.activos.tipo.elegir': 'Pick the type',
  'admin.activos.codigo': 'Code',
  'admin.activos.codigoAyuda': 'Assigned automatically. You can replace it.',
  'admin.activos.identificador': 'Identifier',
  'admin.activos.anio': 'Year',
  'admin.activos.marca': 'Brand',
  'admin.activos.modelo': 'Model',
  'admin.activos.sinMarca': 'No brand',
  'admin.activos.sinModelo': 'No model',
  'admin.activos.modelo.antesMarca': 'Pick the brand first',
  'admin.activos.nuevaMarca': 'New brand',
  'admin.activos.nuevoModelo': 'New model',
  'admin.activos.cliente': 'Customer',
  'admin.activos.cliente.buscar': 'Type the name or the document',
  'admin.activos.cliente.ayuda': 'Type at least two letters to search.',
  'admin.activos.cliente.sinResultados': 'No customer matches.',
  'admin.activos.cliente.resultados': 'Matching customers',
  'admin.activos.cliente.cambiar': 'Change',
  'admin.activos.sinCampos': 'This type asks for no data of its own: what is above is enough.',
  'admin.activos.sinDato': '—',
  'admin.activos.si': 'Yes',
  'admin.activos.no': 'No',
  'admin.activos.notas': 'Notes',
  'admin.activos.eliminar.titulo': 'Delete asset',
  'admin.activos.eliminar.texto':
    'You are about to delete {nombre}. It will no longer show up when opening orders, and its code and identifier become free.',
  'admin.activos.eliminar.boton': 'Delete asset',
  'admin.activos.eliminar.bloqueado.titulo': 'It cannot be deleted',
  'admin.activos.eliminar.bloqueado.activo':
    '{nombre} has {ordenes}. Deleting is only for an asset registered by mistake. If it is no longer serviced, deactivate it: its orders are kept and, when opening a new order, it shows as deactivated.',
  'admin.activos.eliminar.bloqueado.inactivo':
    '{nombre} has {ordenes}. Deleting is only for an asset registered by mistake. It is already deactivated: its orders are kept and, when opening a new order, it shows as such.',
  'admin.activos.eliminar.bloqueado.ordenes': { one: '1 order', other: '{n} orders' },
  'admin.activos.eliminar.entendido': 'Got it',
  'admin.activos.eliminar.desactivar': 'Deactivate asset',
  'admin.activos.aviso.creado': 'The asset was registered',
  'admin.activos.aviso.guardado': 'The asset changes were saved',
  'admin.activos.aviso.activado': '{nombre} is offered again',
  'admin.activos.aviso.desactivado': '{nombre} was deactivated',
  'admin.activos.aviso.eliminado': '{nombre} was deleted',
  'admin.activos.error.titulo': 'Could not complete',
  'admin.activos.error.repetido': '{identificador} is already registered on asset {codigo}.',
  'admin.activos.error.repetidoSinCodigo': '{identificador} is already registered on another asset.',
  'admin.activos.error.verExistente': 'View existing asset →',
  'admin.activos.error.usarExistente': 'Use that asset →',

  // --- Órdenes de servicio (#131) ---
  'admin.ordenes.titulo': 'Service orders',
  'admin.ordenes.listado.info':
    'The vehicles in the shop and who has them. It starts with the active orders of the branch selected above; searching by number, plate or customer shows every match, from any branch and status. Tap a row to open the order.',
  'admin.ordenes.listado.sinSucursal':
    'The board shows the orders of the branch selected above, and you have none. Ask to be assigned to one.',
  'admin.ordenes.listado.buscar': 'Search by number, plate or customer',
  'admin.ordenes.listado.filtro.estado': 'Status',
  'admin.ordenes.listado.filtro.activas': 'Active',
  'admin.ordenes.listado.filtro.todas': 'All',
  'admin.ordenes.listado.filtro.asignado': 'Assigned',
  'admin.ordenes.listado.filtro.todos': 'Everyone',
  'admin.ordenes.listado.filtro.abierta': 'Opened',
  'admin.ordenes.listado.periodo.cualquiera': 'Any time',
  'admin.ordenes.listado.periodo.hoy': 'Today',
  'admin.ordenes.listado.periodo.semana': 'Last 7 days',
  'admin.ordenes.listado.periodo.mes': 'Last 30 days',
  'admin.ordenes.listado.columna.folio': 'Number',
  'admin.ordenes.listado.columna.sucursal': 'Branch',
  'admin.ordenes.listado.columna.estado': 'Status',
  'admin.ordenes.listado.columna.entrega': 'Due',
  'admin.ordenes.listado.retrabajo': 'Rework',
  'admin.ordenes.listado.vencida': 'Overdue',
  'admin.ordenes.listado.vacio.titulo': 'No orders in the shop',
  'admin.ordenes.listado.vacio.texto':
    'Orders of this branch show up here while they are in the shop. Finished ones are under Status: All.',
  'admin.ordenes.listado.sinResultados': 'No order matches «{texto}».',
  'admin.ordenes.volver': 'Back to Service orders',
  'admin.ordenes.nueva.titulo': 'New order',
  'admin.ordenes.nueva.info':
    'Type the plate and pick the vehicle; if it does not exist, register it right here. The rest can be filled in later.',
  'admin.ordenes.nueva.enSucursal': 'At the {sucursal} branch',
  'admin.ordenes.nueva.abrir': 'Open order',
  'admin.ordenes.sinSucursal.titulo': 'Pick a branch',
  'admin.ordenes.sinSucursal.texto':
    'The order opens at the branch selected above, and you have none. Ask to be assigned to one.',
  'admin.ordenes.seccion.vehiculo': 'Vehicle',
  'admin.ordenes.seccion.vehiculoTexto': 'Search by plate, with or without dashes. Its customer becomes the order customer.',
  'admin.ordenes.seccion.cliente': 'Customer',
  'admin.ordenes.seccion.clienteSinVehiculo': 'An order without a vehicle needs its customer.',
  'admin.ordenes.seccion.clienteOpcional':
    'This vehicle has no customer. You can set one now or later; it will be needed to deliver the order.',
  'admin.ordenes.seccion.trabajo': 'The work',
  'admin.ordenes.sinVehiculo': 'Open without a vehicle',
  'admin.ordenes.conVehiculo': 'Search by plate',
  'admin.ordenes.cambiarVehiculo': 'Change',
  'admin.ordenes.placa': 'Plate',
  'admin.ordenes.placa.ejemplo': 'P-123ABC',
  'admin.ordenes.encontrados': 'Vehicles with that plate',
  'admin.ordenes.noExistePlaca': 'There is no vehicle with that plate.',
  'admin.ordenes.registrar': 'Register {placa}',
  'admin.ordenes.desactivado': 'Deactivated',
  'admin.ordenes.clienteInactivo': 'Inactive customer',
  'admin.ordenes.errorBusqueda': 'The plate could not be searched. Please try again in a moment.',
  'admin.ordenes.registradoSinElegir':
    'The vehicle was registered, but it could not be selected. Search for it by its plate.',
  'admin.ordenes.fallaReportada': 'Issue reported by the customer',
  'admin.ordenes.fallaReportada.info': 'What the customer says is wrong, in their own words.',
  'admin.ordenes.asignarA': 'Assign to',
  'admin.ordenes.sinAsignar': 'Unassigned',
  'admin.ordenes.entregaPrometida': 'Promised delivery',
  'admin.ordenes.notas': 'Notes',
  'admin.ordenes.referenciaExterna': 'External reference',
  'admin.ordenes.referenciaExterna.info': 'The document number in another system, such as the ERP invoice.',
  'admin.ordenes.editar.titulo': 'Edit order',
  'admin.ordenes.guardarCambios': 'Save changes',
  'admin.ordenes.aviso.abierta': 'Order opened',
  'admin.ordenes.aviso.guardada': 'Changes saved',
  'admin.ordenes.noExiste.titulo': 'This order does not exist',
  'admin.ordenes.noExiste.texto': 'It may have been deleted, or the address may be mistyped.',
  'admin.ordenes.ficha.vehiculo': 'Vehicle',
  'admin.ordenes.ficha.cliente': 'Customer',
  'admin.ordenes.ficha.sinCliente': 'No customer',
  'admin.ordenes.ficha.asignadoA': 'Assigned to',
  'admin.ordenes.ficha.abierta': 'Opened',
  'admin.ordenes.ficha.por': 'by {persona}',
  'admin.ordenes.ficha.sinFolio': 'Pending',
  'admin.ordenes.ficha.editar': 'Edit',
  'admin.ordenes.ficha.retrabajoDe': 'Rework of order',
  'admin.ordenes.ficha.info':
    'The work order: the vehicle, the customer and where it stands. Edit the header, change its status or mark the next step.',
  'admin.ordenes.mas': 'More actions',
  'admin.ordenes.estado.cambiar': 'Change status',
  'admin.ordenes.estado.marcarComo': 'Mark as {estado}',
  'admin.ordenes.estado.cambiado': 'The order moved to {estado}',
  'admin.ordenes.estado.noSePudo': 'The order could not be changed',
  'admin.ordenes.anular.titulo': 'Move to {estado}',
  'admin.ordenes.anular.texto': 'Write why it is being cancelled. It stays in the order log.',
  'admin.ordenes.anular.motivo': 'Reason',
  'admin.ordenes.anular.boton': 'Cancel order',
  'admin.ordenes.eliminar.boton': 'Delete order',
  'admin.ordenes.eliminar.titulo': 'Delete order {folio}?',
  'admin.ordenes.eliminar.texto':
    'This is for an order that should not exist. Its expenses and photos are hidden too. If the work will not be done, cancel it instead.',
  'admin.ordenes.aviso.eliminada': 'Order deleted',

  // La sección «Trabajo» de la ficha (#133): las líneas de la orden. Sin montos (#137).
  'admin.ordenes.lineas.titulo': 'Work',
  'admin.ordenes.lineas.info':
    'What is done to the vehicle: services, products and notes. Check a line when it is done; uncheck it if that was a mistake.',
  'admin.ordenes.lineas.cumplidas': {
    one: '{hechas} of {n} done',
    other: '{hechas} of {n} done',
  },
  'admin.ordenes.lineas.anotaciones': {
    one: '{n} note',
    other: '{n} notes',
  },
  'admin.ordenes.lineas.agregar': 'Service or product',
  'admin.ordenes.lineas.anotar': 'Note',
  'admin.ordenes.lineas.vacio': 'No work recorded yet',
  'admin.ordenes.lineas.vacioTexto': 'Add the services and products of this order, or leave a note.',
  'admin.ordenes.lineas.noSePudo': 'The line could not be changed',
  'admin.ordenes.lineas.clase.service': 'Service',
  'admin.ordenes.lineas.clase.product': 'Product',
  'admin.ordenes.lineas.clase.note': 'Note',
  'admin.ordenes.lineas.dictado': 'Dictated: “{texto}”',
  'admin.ordenes.lineas.cumplidaPor': 'Done by {persona}',
  'admin.ordenes.lineas.entregadaPor': 'Handed over by {persona}',
  'admin.ordenes.lineas.asignadaA': 'Assigned to {persona} · pending',
  'admin.ordenes.lineas.pendiente': 'Pending',
  'admin.ordenes.lineas.cumplida': 'Done: {linea}',
  'admin.ordenes.lineas.acciones': 'Actions for {linea}',
  'admin.ordenes.lineas.cantidad': 'Quantity',
  'admin.ordenes.lineas.cambiarCantidad': 'Change quantity',
  'admin.ordenes.montos.total': 'Total',
  'admin.ordenes.montos.sinPrecio': 'No price',
  'admin.ordenes.montos.error': 'Could not load the amounts',
  'admin.ordenes.montos.incompleto': 'Incomplete total',
  'admin.ordenes.montos.lineasSinPrecio': {
    one: '{n} line without a price',
    other: '{n} lines without a price',
  },
  'admin.ordenes.montos.fijado': 'Set by hand',
  'admin.ordenes.montos.cambiarPrecio': 'Change price',
  'admin.ordenes.montos.precioUnitario': 'Unit price',
  'admin.ordenes.montos.precioInfo':
    'The price of one unit: the line charges price times quantity. Changing it here does not touch the catalog list price. Leave it empty if it has no price yet.',
  'admin.ordenes.montos.cambiarTotal': 'Change total',
  'admin.ordenes.montos.totalInfo':
    'Enter the total agreed with the customer —a fixed price, a discount— and it will stop following the sum of the lines. Leave it empty to go back to the sum.',
  'admin.ordenes.montos.motivo': 'Reason',
  'admin.ordenes.montos.motivoInfo':
    'Once the order is finalized, changing the total requires a reason. It is kept in the log next to the previous total.',
  'admin.ordenes.lineas.asignar': 'Assign to someone else',
  'admin.ordenes.lineas.asignar.titulo': 'Assign the line',
  'admin.ordenes.lineas.asignar.info':
    'For when the vehicle is split among several people. With no one chosen, the line belongs to whoever has the order.',
  'admin.ordenes.lineas.asignar.nadie': 'Whoever has the order',
  'admin.ordenes.lineas.ligar': 'Link to catalog',
  'admin.ordenes.lineas.ligar.info':
    'It becomes the catalog service or product and keeps what was written. If it had no price, it takes the list price.',
  'admin.ordenes.lineas.ligar.campo': 'Service or product',
  'admin.ordenes.lineas.ligar.ejemplo': 'Search by name or code',
  'admin.ordenes.lineas.ligar.boton': 'Link',
  'admin.ordenes.lineas.quitar': 'Remove line',
  'admin.ordenes.lineas.agregarFoto': 'Add photo',
  'admin.ordenes.lineas.quitar.titulo': 'Remove this line?',
  'admin.ordenes.lineas.quitar.texto': '“{linea}” stops counting in the order. What it said is kept in the log.',
  'admin.ordenes.lineas.agregar.titulo': 'Add a service or product',
  'admin.ordenes.lineas.agregar.info':
    'Type and pick a service or product from the catalog: it takes its price. If it is not there, say what it is and it is added without a price; you set it later.',
  'admin.ordenes.lineas.agregar.campo': 'Service or product',
  'admin.ordenes.lineas.agregar.ejemplo': 'Oil change',
  'admin.ordenes.lineas.agregar.boton': 'Add',
  'admin.ordenes.lineas.libre.clase': 'If it is not in the catalog, what is it?',
  'admin.ordenes.lineas.libre.info':
    'It is added with what you typed and without a price. If you add it to the catalog later, link it from the line menu.',
  'admin.ordenes.lineas.anotar.titulo': 'Add a note',
  'admin.ordenes.lineas.anotar.info':
    'Something that happened during the work and is neither charged nor completed: "the belt looked worn".',
  'admin.ordenes.lineas.anotar.campo': 'Note',
  'admin.ordenes.lineas.anotar.ejemplo': 'The belt looked worn',
  'admin.ordenes.lineas.cambiarItem': 'Change',
  'admin.ordenes.lineas.resultados': 'Catalog matches',
  'admin.ordenes.lineas.errorBusqueda': 'The catalog could not be searched. Try again in a moment.',
  'admin.ordenes.lineas.cerrar': 'Close',
  'admin.ordenes.lineas.aviso.agregada': 'Line added',
  'admin.ordenes.lineas.aviso.ligada': 'Line linked to the catalog',
  'admin.ordenes.lineas.aviso.quitada': 'Line removed',
  'admin.ordenes.lineas.aviso.fotoAgregada': {
    one: 'Photo added to “{linea}”',
    other: '{n} photos added to “{linea}”',
  },
  'admin.ordenes.fotos.titulo': 'Photos',
  'admin.ordenes.fotos.info':
    'Photos of the order and its lines. “Add” uploads to the order in general; a photo of a job is added from its line’s “•••” menu, and shows the line’s name below.',
  'admin.ordenes.fotos.deLaOrden': 'From the order',
  'admin.ordenes.historial.info':
    'What happened to the order, newest first: when it was opened and with what details, each status change, changes to the customer, vehicle, assignee, promised delivery and reported issue, the total set by hand, the lines added or removed and the photos added or deleted, with who did it. Lines one person adds one after another go together on one row; changes to a line’s quantity or price are not shown.',
  'admin.ordenes.historial.abierta': 'The order was opened',
  'admin.ordenes.historial.estado': 'The status changed',
  'admin.ordenes.historial.lineaQuitada': 'Line removed',
  'admin.ordenes.historial.lineasAgregadas': {
    one: 'Line added',
    other: 'Lines added',
  },
  'admin.ordenes.historial.totalFijado': 'Total set by hand',
  'admin.ordenes.historial.totalALaSuma': 'The total follows the sum of the lines again',
  'admin.ordenes.historial.totalDeA': '{a} (was {de})',
  'admin.ordenes.historial.antes': 'Was {de}',
  'admin.ordenes.historial.motivo': 'Reason: {motivo}',
  'admin.ordenes.historial.enLaLinea': 'On the line «{linea}»',
  'admin.ordenes.historial.sinVehiculo': 'No vehicle',
  'admin.ordenes.historial.sinFecha': 'No date',

  // --- The history of any record (#181) ---
  'admin.historial.titulo': 'History',
  'admin.historial.vacio': 'Nothing in the history yet.',
  'admin.historial.sinAutor': 'Unknown',
  'admin.historial.creado': 'Created',
  'admin.historial.activado': 'Activated',
  'admin.historial.desactivado': 'Deactivated',
  'admin.historial.pagado': 'Marked paid',
  'admin.historial.pagoQuitado': 'Paid mark removed',
  'admin.historial.editado': 'Details edited',
  'admin.historial.cambio': '{campo}: {de} → {a}',
  'admin.historial.valor': '{campo}: {a}',
  'admin.historial.cambioSinValor': 'Changed: {campos}',
  'admin.historial.sinValor': '—',
  'admin.historial.eliminado': 'Deleted',
  'admin.historial.conDetalle': '{que}: {cual}',
  'admin.historial.contacto.agregado': 'Contact added',
  'admin.historial.contacto.quitado': 'Contact removed',
  'admin.historial.contacto.editado': 'Contact edited',
  'admin.historial.contacto.principal': 'New primary contact',
  'admin.historial.activo.agregado': 'Asset added',
  'admin.historial.activo.quitado': 'Asset removed',
  'admin.historial.activo.pasoA': 'Moved to',
  'admin.historial.adjunto.fotoAgregada': 'Photo added',
  'admin.historial.adjunto.fotoEliminada': 'Photo deleted',
  'admin.historial.adjunto.archivoAgregado': 'File added',
  'admin.historial.adjunto.archivoEliminado': 'File deleted',

  // --- Página no encontrada ---
  'admin.noEncontrada.titulo': 'This page does not exist',
  'admin.noEncontrada.texto': 'Check the address, or go back home from the menu above.',

  'plataforma.marca': 'TallerYa · Platform',
  'plataforma.nav.inicio': 'Home',
  'plataforma.sesion.entrar': 'Sign in',
  'plataforma.sesion.salir': 'Sign out',
  'plataforma.acceso.sinSesion.titulo': 'Platform console',
  'plataforma.acceso.sinSesion.texto': 'For the TallerYa team only. Sign in with your account.',
  'plataforma.acceso.ajeno.titulo': 'This console is not for your account',
  'plataforma.acceso.ajeno.texto':
    'It is for the TallerYa team only. If you work at a shop, your place is the shop admin site.',
  'plataforma.acceso.error.titulo': 'Could not sign in',
  'plataforma.acceso.error.texto':
    'Keycloak did not complete the sign-in. Try again; if it keeps failing, check that your account has the platform-admin role.',
  'plataforma.inicio.titulo': 'Platform console',
  'plataforma.inicio.entrada': 'This is where shops are created and where all of them can be seen.',
  'plataforma.inicio.aviso.titulo': 'No screens yet',
  'plataforma.inicio.aviso.texto':
    'This is the shell of the console: sign-in, navigation and deployment. Creating a shop and the list of shops come in the next tasks.',
  'plataforma.noEncontrada.titulo': 'This page does not exist',
  'plataforma.noEncontrada.texto': 'Check the address, or go back home from the menu above.',

  'taller.inicio.titulo': 'TallerYa',
  'taller.inicio.entrada':
    'This is the shell of the mechanic app. The button saves a test action, which goes to the server when there is signal.',
  'taller.inicio.probar': 'Test sending',
  'taller.inicio.guardada': 'Saved on the phone.',
  'taller.inicio.noGuardada': 'It could not be saved on the phone. Try again.',
  'taller.inicio.clave': 'Key: {clave}',
  'taller.inicio.almacenamiento': 'What this phone stores',
  'taller.inicio.comprobando': 'Checking…',
  'taller.inicio.instalada': 'Opened from the home screen icon.',
  'taller.inicio.enNavegador': 'Opened in the browser, not from the home screen icon.',
  'taller.inicio.protegido': 'Protected: the phone will not clear it on its own.',
  'taller.inicio.desprotegido': 'Not protected: the phone may clear it when space runs low.',
  'taller.inicio.protegidoSinApi': 'This browser cannot protect it.',
  'taller.inicio.protegidoError': 'The phone returned an error when asked to protect it ({error}).',
  'taller.inicio.espacio': 'Storage quota: {mb} MB.',

  'taller.cola.pendientes': { one: '1 pending sync', other: '{n} pending sync' },
  'taller.cola.alDia': 'All synced',
  'taller.cola.rechazadas': { one: '1 was not accepted', other: '{n} were not accepted' },
  'taller.cola.porDecidir':
    'This was sent more than 90 days ago and we do not know if it arrived. If you send it again, it may be duplicated.',
  'taller.cola.trabada':
    'This is not going through: the server fails every time. It keeps trying on its own, and what comes after waits. Tell your administrator; if you discard it, it is not sent.',
  'taller.cola.enviar': 'Send',
  'taller.cola.descartar': 'Discard',
  'taller.cola.errorDecidir': 'It could not be done on the phone. Try again.',

  // --- Errores que manda la API (B-21) ---
  'error.desconocido': 'Something unexpected went wrong',
  'error.auth.unauthenticated': 'Your session expired. Please sign in again.',
  'error.auth.forbidden': 'You are not allowed to do this',
  'error.auth.no_tenant': 'Your user is not assigned to a workshop',
  'error.auth.tenant_suspended':
    'This workshop is suspended. Whoever manages it can contact TallerYa to reactivate it',
  'error.auth.device_session_invalid':
    'The session on this phone is no longer valid. Register it again with a QR',
  'error.auth.qr_no_aceptado':
    'This QR code does not work. If it was just shown to you, wait a few seconds; otherwise ask for a new one',
  'error.auth.no_membership': 'Your user does not belong to this workshop',
  'error.unexpected': 'Something unexpected went wrong',
  'error.request.invalid': 'The request could not be read',
  'error.request.validation': 'The submitted data is not valid',
  'error.validation.required': 'This field is required',
  'error.validation.too_long': 'It is too long',
  'error.validation.too_short': 'It is too short',
  'error.validation.length': 'It does not have the expected length',
  'error.validation.too_small': 'It is too small',
  'error.validation.too_large': 'It is too large',
  'error.validation.out_of_range': 'It is out of the allowed range',
  'error.validation.email': 'It is not a valid email',
  'error.validation.format': 'It does not have the expected format',
  'error.validation.mismatch': 'It does not match',
  'error.validation.invalid': 'It is not valid',
  'error.validation.too_many': 'There are too many',
  'error.validation.phone': 'Use only numbers, spaces and + ( ) -',
  'error.document.numero_no_calza': 'The number does not match the format of that document',
  'error.validation.numero_coma': 'Check the number: use a period for decimals',
  'error.ping.mensaje_prohibido': 'That message cannot be used to test the system',

  'error.staff.no_encontrado': 'That person does not exist in this workshop',
  'error.staff.codigo_tomado':
    'That employee code is already in use. It stays taken even if the person was deleted, because their user still exists in the identity system.',
  'error.staff.cuenta_tomada': 'That account is already linked to another person in this workshop',
  'error.staff.no_se_elimina_solo':
    'You cannot remove yourself from the workshop. Ask another administrator to do it.',
  'error.staff.tiene_historial':
    'This person already has work recorded in the workshop and cannot be deleted. You can deactivate them',
  'error.staff.correlativo_agotado':
    'Could not propose a free employee code. Try again or type one.',
  'error.staff.sucursal_no_encontrada':
    'One of those branches does not exist in this workshop, or was deleted',

  'error.staff_pin.ya_tiene_acceso': 'This person already has a PIN. If they forgot it, reset it.',
  'error.staff_pin.sin_acceso': 'This person does not have access yet. Create it first.',
  'error.staff_pin.persona_inactiva': 'This person is deactivated. Activate them before giving them access.',
  'error.staff_pin.sin_ficha': 'Your account has no staff record in this workshop',
  'error.staff_pin.incorrecto': 'The PIN is not correct',
  'error.staff_pin.igual_al_actual': 'The new PIN must be different from the current one',
  'error.staff_pin.solo_otro_dueno': "Only another owner can set or reset the shop owner's PIN",
  'error.staff_pin.en_espera': 'Too many wrong PINs. Wait 15 minutes and try again',
  'error.device.qr_no_valido': 'This QR code no longer works. Ask the administrator for a new one',
  'error.device.qr_solo_empleados':
    'The QR code is for an employee to register their phone. Shop managers sign in to the admin site with their own account',
  'error.device.no_valido': 'This phone is no longer registered in the workshop. Register it again',
  'error.device.no_encontrado': 'That phone does not exist in this workshop',
  'error.staff_pin.formato': 'The PIN is 4 digits',

  'error.identity.user.username_taken_by_other_tenant':
    'That username is already used by another workshop',
  'error.identity.membership.unknown_role': 'The requested role does not exist',
  'error.identity.membership.revoked':
    "That person was removed from this workshop. Restoring access is an administrator's decision.",
  'error.identity.staff.already_linked': 'That person already has another account linked',
  'error.identity.staff.account_taken':
    'That account is already linked to another person in this workshop',
  'error.identity.staff.account_missing':
    'The account linked to that person no longer exists',
  'error.identity.staff.account_other_tenant':
    'The account linked to that person belongs to another workshop',
  'error.identity.user.username_taken':
    'The username that belongs to that person is already taken by another account',
  'error.identity.tenant.not_found': 'The workshop does not exist',
  'error.identity.user.email_required':
    'That person has no email, and needs one to sign in to the system',
  'error.identity.user.email_taken_by_other_account':
    'That email already belongs to the account of another person in the workshop',
  'error.identity.enrollment.personal_account':
    'This person signs in with their email: the enrollment QR is only for mechanic accounts',

  'error.tenant.slug_tomado': 'A workshop with that slug already exists',
  'error.tenant.codigo_tomado': 'A workshop with that code already exists',
  'error.tenant.id_ocupado':
    'That id already belongs to another workshop. A retried sign-up must carry the same data.',

  'error.settings.no_existe': 'That setting does not exist',
  'error.settings.nivel_no_permitido':
    'That setting cannot be set at that level, only at a more general one.',
  'error.settings.valor_invalido':
    'That value does not match the setting type, or is not one of its options',
  'error.settings.no_editable':
    'That setting is not changed by the workshop. Please ask support.',
  'error.settings.escritura_simultanea':
    'Someone else changed that setting at the same time. Please save again.',

  'error.role_permission.locked':
    'That permission cannot be taken from the owner: without it, nobody in the workshop could give it back.',
  'error.role_permission.role_not_found': 'That role does not exist',
  'error.role_permission.permission_not_found': 'That permission does not exist',

  'error.specialty.duplicada':
    'Someone else saved a specialty with that same name at the same time. Please try again.',
  'error.specialty.no_encontrada': 'That specialty does not exist in this workshop',
  'error.specialty.nombre_duplicado': 'A specialty with that name already exists in this workshop',
  'error.specialty.en_uso':
    'Some staff record has that specialty, even a deleted one: deactivate it instead of deleting it.',
  'error.location.codigo_tomado': 'That branch code is already in use in this workshop',
  'error.location.nombre_duplicado': 'A branch with that name already exists in this workshop',
  'error.location.principal_simultanea':
    'Someone else changed the main branch at the same time. Please try again.',
  'error.location.no_encontrada': 'That branch does not exist in this workshop',
  'error.location.correlativo_agotado':
    'Could not propose a free branch code. Try again or type one.',
  'error.location.principal_no_se_desactiva':
    'The main branch cannot be taken out of service. Make another branch the main one first.',
  'error.location.principal_no_se_elimina':
    'The main branch cannot be deleted. Make another branch the main one first.',
  'error.location.ultima_no_se_elimina': 'The only branch of the workshop cannot be deleted',
  'error.location.inactiva_no_es_principal':
    'A branch that is out of service cannot be the main one. Put it back in service first.',
  'error.catalog_item.codigo_tomado': 'That code is already in use in this workshop catalog',
  'error.catalog_item.no_encontrado': 'That product or service does not exist in this workshop',
  'error.catalog_item.en_ordenes': 'This product or service appears in orders and cannot be deleted. You can deactivate it',
  'error.catalog_item.correlativo_agotado':
    'Could not propose a free code. Try again or type one yourself',
  'error.catalog_item.categoria_no_disponible': 'That category does not exist or is no longer active',
  'error.catalog_item.duracion_solo_servicios': 'Reference duration is only for services',
  'error.catalog_item.nombre_duplicado':
    'Another item with that name and of the same kind — service or product — already exists in this workshop',
  'error.catalog_import.formato_no_soportado': 'The file must be Excel (.xlsx) or CSV',
  'error.catalog_import.archivo_ilegible':
    'The file could not be read. Check that it is an Excel or CSV file and try again',
  'error.catalog_import.archivo_vacio': 'The file has no rows to import',
  'error.catalog_import.demasiadas_filas': 'The file has too many rows. Split it and try again',
  'error.catalog_import.columnas_faltantes':
    'Columns are missing: the file must have at least the «Tipo» and «Nombre» columns, with those Spanish headers. Use the template',
  'error.catalog_import.tipo_requerido': 'The type is missing: service or product',
  'error.catalog_import.tipo_invalido': 'The type must be service or product',
  'error.catalog_import.nombre_requerido': 'The name is missing',
  'error.catalog_import.nombre_largo': 'The name is too long',
  'error.catalog_import.codigo_largo': 'The code is too long',
  'error.catalog_import.categoria_larga': 'The category name is too long',
  'error.catalog_import.precio_invalido': 'The price is not a valid number',
  'error.catalog_import.precio_decimales': 'The price allows up to six decimal places',
  'error.catalog_import.duracion_invalida': 'The duration must be a whole number of minutes, greater than zero',
  'error.catalog_import.codigo_repetido': 'That code is already in another row of the file',
  'error.catalog_import.nombre_repetido': 'That name is already in another row of the file, for the same type',
  'error.catalog_import.categoria_inactiva': 'That category is deactivated in this workshop',
  'error.catalog_category.nombre_duplicado':
    'A category with that name already exists in this workshop',
  'error.catalog_category.no_encontrada': 'That category does not exist in this workshop',
  'error.catalog_category.en_uso':
    'Some product or service has that category, even a deleted one: deactivate it instead of deleting it.',
  'error.asset_brand.nombre_duplicado':
    'A brand with that name already exists for that asset type in this workshop',
  'error.asset_model.nombre_duplicado':
    'That brand already has a model with that name in this workshop',
  'error.asset_brand.no_encontrada': 'That brand does not exist in this workshop',
  'error.asset_model.no_encontrado': 'That model does not exist in this workshop',
  'error.asset_type.no_encontrado': 'That asset type does not exist in this workshop',
  'error.asset_type.apagado': 'That asset type is deactivated: it is not offered for new assets',
  'error.asset.no_encontrado': 'That asset does not exist in this workshop',
  'error.asset.cliente_no_encontrado': 'That customer does not exist in this workshop',
  'error.asset.cliente_inactivo':
    '{customerName} is inactive: assets cannot be assigned to them. Activate them before continuing.',
  'error.asset.marca_de_otro_tipo': 'That brand belongs to another asset type',
  'error.asset.modelo_de_otra_marca': 'That model does not belong to the chosen brand',
  'error.asset_brand.apagada': 'That brand is deactivated: pick another one',
  'error.asset_model.apagado': 'That model is deactivated: pick another one',
  'error.asset.identificador_requerido': 'This asset type requires it',
  'error.asset.tiene_historial': 'This asset has registered orders and cannot be deleted. You can deactivate it',
  'error.asset.correlativo_agotado':
    'No free code could be proposed. Try again or type one by hand',
  'error.asset.detalle.no_es_opcion': 'Pick one of the options on the list',
  'error.asset.detalle.desconocido': 'This asset type does not have that field',
  'error.asset.detalle.apagado': 'That field is no longer asked for this asset type',
  'error.asset_type.nombre_duplicado': 'An asset type with that name already exists in this workshop',
  'error.asset_type_field.no_encontrado': 'That asset type does not have that field',
  'error.asset_type_field.lista_sin_opciones': 'A list needs at least one option',
  'error.asset_type_field.opciones_repetidas': 'Two options have the same name',
  'error.asset.codigo_tomado': 'Another asset in this workshop already has that code',
  'error.asset.identificador_tomado':
    'Another asset of that type in this workshop already has that identifier',
  'error.asset_type.codigo_tomado': 'An asset type with that code already exists in this workshop',
  'error.asset_type_field.codigo_repetido': 'That asset type already has a field with that code',
  'error.customer.codigo_tomado': 'Another customer in this workshop already has that code',
  'error.customer.documento_tomado':
    'Another customer in this workshop already has that document',
  'error.customer.telefono_repetido': 'This customer already has that phone number',
  'error.customer.correo_repetido': 'This customer already has that email',
  'error.customer.principal_simultaneo':
    'Someone else changed the primary contact at the same time. Please try again.',
  'error.customer.no_encontrado': 'That customer does not exist in this workshop',
  'error.customer.correlativo_agotado':
    'Could not suggest a free customer code. Please try again or type one in.',
  'error.customer.documento_no_aplica': 'That document type does not apply to this customer',
  'error.customer.nombre_comercial_solo_empresa': 'Only a company has a trade name',
  'error.customer.tiene_historial':
    'This customer has orders or assets on record and cannot be deleted. You can deactivate them.',
  'error.customer_export.demasiadas_filas':
    'More than 10000 customers match these filters. Narrow them down and try again.',
  'error.customer_import.formato_no_soportado': 'The file must be Excel (.xlsx) or CSV',
  'error.customer_import.archivo_muy_grande': 'The file is larger than 5 MB',
  'error.customer_import.archivo_ilegible':
    'The file could not be read. Check that it is the template saved as Excel or CSV.',
  'error.customer_import.demasiadas_filas':
    'The file has more than 5000 customers. Split it into several files.',
  'error.customer_import.columnas_faltantes':
    'The file is missing the tipo and nombre columns. Download the template and copy your data into it.',
  'error.customer_import.sin_filas': 'The file has no customers',
  'error.customer_import.tipo_invalido': 'Must be Persona or Empresa',
  'error.customer_import.tipo_documento_desconocido':
    'That document type is not one the workshop uses',
  'error.customer_import.codigo_repetido_en_archivo': 'Another row in the file has that code',
  'error.customer_import.documento_repetido_en_archivo':
    'Another row in the file has that document',
  'error.customer_import.lote_deshecho':
    'Not saved: someone else registered a matching customer while importing',
  'error.supplier.codigo_tomado': 'Another supplier in this workshop already has that code',
  'error.supplier.nombre_duplicado': 'A supplier with that name already exists in this workshop',
  'error.supplier.documento_tomado': 'Another supplier in this workshop already has that document',
  'error.supplier.nombre_y_documento_duplicados':
    'A supplier with that name and another with that document already exist in this workshop',
  'error.supplier.no_encontrado': 'That supplier does not exist in this workshop',
  'error.supplier.documento_no_aplica': 'That document type is not available in this workshop',
  'error.supplier.correlativo_agotado':
    'Could not propose a free supplier code. Try again or type one.',
  'error.attachment.no_encontrado': 'That attachment does not exist in this workshop',
  'error.attachment.activo_no_encontrado': 'That asset does not exist in this workshop',
  'error.attachment.orden_no_encontrada': 'That order does not exist in this workshop',
  'error.attachment.linea_no_encontrada': 'That line does not exist in that order',
  'error.attachment.gasto_no_encontrado': 'That expense does not exist in this workshop',
  'error.attachment.tipo_no_permitido': 'Only photos (JPEG, PNG, WebP, HEIC) and PDF are accepted',
  'error.attachment.demasiado_grande': 'The file is larger than 20 MB',
  'error.attachment.contenido_no_coincide': 'The file is not the type it claims to be',
  'error.attachment.id_en_uso': 'That attachment was already uploaded for something else',
  'error.attachment.fecha_fuera_de_rango': "The photo's date is not valid: check the device clock",
  'error.order_status.nombre_duplicado': 'An order status with that name already exists in this workshop',
  'error.order_status.inicial_simultaneo':
    'Someone else changed the initial status at the same time. Please try again.',
  'error.order_status.no_encontrado': 'That order status does not exist in this workshop',
  'error.order_status.inicial_no_se_desactiva':
    'The initial status cannot be deactivated: every order starts in it. Make another one initial first.',
  'error.order_status.ultimo_de_su_etapa':
    'It is the last active status of its stage: without it, no order could go through that stage.',
  'error.order_status.inicial_solo_abierto':
    'The initial status belongs to the Open stage. Make another one initial first, then change its stage.',
  'error.order_status.solo_abierto_es_inicial': 'Only a status of the Open stage can be the initial one',
  'error.order_status.inactivo_no_es_inicial': 'A deactivated status cannot be the initial one. Activate it first.',
  'error.order_status.orden_desactualizado':
    'The list of statuses changed while you were reordering it. Please try again.',
  'error.order_status.con_ordenes':
    'This status has orders: its stage and whether it freezes the amounts cannot change. Move its orders to another status first, or create a new one in the stage you want.',
  'error.service_order.folio_tomado': 'Another order in this workshop already has that order number',
  'error.service_order.no_encontrada': 'That order does not exist in this workshop',
  'error.service_order.folio_agotado':
    'No free order number could be assigned. Check the order number format in Settings.',
  'error.service_order.sucursal_no_encontrada': 'That branch does not exist in this workshop',
  'error.service_order.sucursal_apagada': 'That branch is deactivated: it does not open new orders',
  'error.service_order.cliente_no_encontrado': 'That customer does not exist in this workshop',
  'error.service_order.cliente_inactivo':
    '{customerName} is inactive: orders cannot be assigned to them. Activate them before continuing.',
  'error.service_order.activo_no_encontrado': 'That asset does not exist in this workshop',
  'error.service_order.personal_no_encontrado': 'That person is not on this workshop staff',
  'error.service_order.personal_apagado': 'That person is deactivated: orders cannot be assigned to them',
  'error.service_order.orden_anterior_no_encontrada': 'The previous order does not exist in this workshop',
  'error.service_order.sin_cliente_ni_activo': 'An order needs a customer or an asset',
  'error.service_order.falta_cliente': 'The order needs a customer before it can be delivered',
  'error.service_order.falta_folio': 'The order needs an order number before it can be delivered; it arrives when it syncs',
  'error.service_order.falta_activo': 'The order needs an asset before it can be delivered: the workshop requires it',
  'error.service_order.falta_motivo_de_anulacion': 'Cancelling the order requires a reason',
  'error.service_order.estado_apagado': 'That status is deactivated',
  'error.service_order.montos_congelados':
    'The order is finalized and its amounts do not change. Move it back to a non-final status to edit them.',
  'error.service_order.total_sin_motivo': 'Changing the total of a finalized order requires a reason',
  'error.service_order.anulada': 'A cancelled order has no total to change',
  'error.service_order.finalizada': 'A finalized order cannot be deleted. Move it back to a non-final status first.',
  'error.service_order.anulada_no_se_entrega':
    'A cancelled order cannot be delivered directly. Reopen it in a work status first.',
  'error.service_order.estado_no_encontrado': 'That order status does not exist in this workshop',
  'error.service_order.linea_no_encontrada': 'That line is not on the order',
  'error.service_order.linea_repetida': 'A line with that identifier already exists',
  'error.service_order.monto_fuera_de_rango':
    'The amount is larger than the system can store. Check the quantity and the price.',
  'error.service_order.item_no_encontrado': 'That service or product does not exist in the shop catalog',
  'error.service_order.item_apagado': 'That service or product is deactivated in the catalog',
  'error.service_order.linea_ya_ligada': 'That line is already a service or product from the catalog',
  'error.service_order.linea_es_anotacion': 'A note has no quantity or price, and is not marked as done',
  'error.service_order.sin_ficha_de_personal':
    'Your user has no staff record in this shop: ask for it to be linked to mark work as done',
  'error.expense.factura_repetida': 'That invoice is already recorded as an expense in this workshop',
  'error.expense.no_encontrado': 'That expense does not exist in this workshop',
  'error.expense.sucursal_no_encontrada': 'That location does not exist in this workshop',
  'error.expense.sucursal_apagada': 'That location is deactivated: it does not record new expenses',
  'error.expense.orden_no_encontrada': 'That order does not exist in this workshop',
  'error.expense.proveedor_no_encontrado': 'That supplier does not exist in this workshop',
  'error.expense.proveedor_apagado': 'That supplier is deactivated: new expenses cannot be charged to it',
  'error.expense.pago_antes_de_compra': 'The payment date cannot be earlier than the purchase date',
  'error.expense.fecha_fuera_de_rango': 'Check the date: it does not look like a purchase or payment date',
  'error.conflict.duplicado': 'A record with that data already exists',
  'error.conflict.cambio_simultaneo':
    'Someone else saved changes to this record while you were editing it. Reload to see the current version and make your changes again: what you typed here was not saved.',
  'error.idempotency.clave_invalida': 'The request came with an invalid retry key',
  'error.idempotency.clave_reusada': 'This request does not match the one sent earlier with the same key',
  'error.idempotency.en_curso': 'This request is already being processed. Please try again in a moment.',
};
